import React, { useEffect, useState, useRef, useCallback, KeyboardEvent } from 'react';
import {
    CollectionModel,
    CollectionShare,
    CollectionSharePermission,
    FileInfo,
    ShareableUser,
} from '../types/models';
import { useDocumentStore } from '../stores/documentStore';
import { useToastStore } from '../stores/ToastStore';
import { useContextMenu } from '../hooks/useContextMenu';
import { ContextMenuPortal } from './common/ContextMenuPortal';
import { FileNode, FileBrowserComponent } from '../types/files';
import { useFileUpload, FileDetails } from '../hooks/useFileUpload';
import DragAndDropModal, { DragAndDropResult } from './DragAndDropModal';
import { apiClient } from '../clients/APIClient';
import { downloadMagazine, generateMagazineFilename } from '../utils/magazineExport';
import { useMagazineImport } from '../hooks/useMagazineImport';
import { useBackendOptions } from '../stores/settingsRegistry';
import { useChatManagementStore } from '../stores/chatManagementStore';

interface CollectionsModalProps {
    toggleModal: () => void;
    FileBrowser?: FileBrowserComponent;
}

// Using CollectionModel from backend, extending with documentCount and loaded files
interface CollectionTile extends CollectionModel {
    documentCount?: number;
    files?: FileInfo[];
}

function randomIdSegment(): string {
    const browserCrypto = globalThis.crypto;
    if (browserCrypto && typeof browserCrypto.randomUUID === 'function') {
        return browserCrypto.randomUUID().slice(0, 8);
    }
    if (browserCrypto && typeof browserCrypto.getRandomValues === 'function') {
        const bytes = new Uint8Array(4);
        browserCrypto.getRandomValues(bytes);
        return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    }
    return Math.random().toString(16).slice(2, 10).padEnd(8, '0');
}

function generateCollectionId(): string {
    const now = new Date();
    return `Collection_${now.toLocaleDateString().replaceAll('/', '-')}_${randomIdSegment()}`;
}


export function CollectionsModal({ toggleModal, FileBrowser }: CollectionsModalProps) { 
    const { fetchDocuments, updateDocumentContext } = useDocumentStore();
    const { showToast, setModalOpen } = useToastStore();

    
    // Use the custom hook for context menu logic
    const { contextMenuOpen, toggleContextMenu, closeContextMenu, menuPosition, menuRef, handleMenuAction } = useContextMenu();
    
    // Track modal state for toast and TTS positioning
    useEffect(() => {
        setModalOpen(true);
        return () => setModalOpen(false);
    }, [setModalOpen]);
    
    const [collections, setCollections] = useState<CollectionTile[]>([]);
    const [documents, setDocuments] = useState<FileInfo[]>([]);
    const [documentsToRemove, setDocumentsToRemove] = useState<string[]>([]);
    const [_originalDocuments, setOriginalDocuments] = useState<FileInfo[]>([]);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
    const [documentToDelete, setDocumentToDelete] = useState<FileInfo | null>(null);
    const [tempCollection, setTempCollection] = useState<CollectionTile | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    // Collection deletion state
    const [showDeleteCollectionConfirmation, setShowDeleteCollectionConfirmation] = useState<boolean>(false);
    const [collectionToDelete, setCollectionToDelete] = useState<CollectionTile | null>(null);

    const [selectedCollection, setSelectedCollection] = useState<CollectionTile | null>(null);
    const [editingName, setEditingName] = useState<boolean>(false);
    const [editingDescription, setEditingDescription] = useState<boolean>(false);
    const [tempName, setTempName] = useState<string>('');
    const [tempDescription, setTempDescription] = useState<string>('');
    const [tempIsShared, setTempIsShared] = useState<boolean>(false);
    const [hasChanges, setHasChanges] = useState<boolean>(false);

    // Sharing. A collection is granted to named users; "Public" is a separate,
    // admin-only setting that exposes it to every account on the instance.
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [isPiiPhiEligible, setIsPiiPhiEligible] = useState<boolean>(false);
    const [shares, setShares] = useState<CollectionShare[]>([]);
    const [shareableUsers, setShareableUsers] = useState<ShareableUser[]>([]);
    const [shareUserId, setShareUserId] = useState<string>('');
    const [sharePermission, setSharePermission] = useState<CollectionSharePermission>('view');
    const [sharesLoading, setSharesLoading] = useState<boolean>(false);
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    
    // Files added during this editing session (provisional changes)
    const [addedFiles, setAddedFiles] = useState<Set<string>>(new Set());
    const [addedFileDetails, setAddedFileDetails] = useState<Map<string, FileDetails>>(new Map());
    
    // Upload progress state
    const { uploadProgress, uploadFiles, cancelUpload, isUploading: _isUploading, resetProgress: _resetProgress, uploadBase64File } = useFileUpload();
    const [totalFilesToUpload, setTotalFilesToUpload] = useState<number>(0);
    const [currentFileIndex, setCurrentFileIndex] = useState<number>(1);
    const [isDragDropUpload, setIsDragDropUpload] = useState<boolean>(false);

    // New collection creation state
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [newCollectionName, setNewCollectionName] = useState<string>('');
    const [newCollectionDescription, setNewCollectionDescription] = useState<string>('');
    const [newCollectionIsShared, setNewCollectionIsShared] = useState<boolean>(false);
    const [newCollectionIsPiiPhiControlled, setNewCollectionIsPiiPhiControlled] = useState<boolean>(false);
    const [creatingCollection, setCreatingCollection] = useState<boolean>(false);
    
    // Constants for input limits
    const MAX_COLLECTION_NAME_LENGTH = 40;
    const MAX_COLLECTION_DESCRIPTION_LENGTH = 200;
    
    // Scroll navigation state
    const [showLeftArrow, setShowLeftArrow] = useState<boolean>(false);
    const [showRightArrow, setShowRightArrow] = useState<boolean>(false);

    // Bulk operations state
    const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
    const [showBulkDeleteConfirmation, setShowBulkDeleteConfirmation] = useState<boolean>(false);
    const [bulkOperation, setBulkOperation] = useState<'remove' | 'delete' | null>(null);
    
    // Delete operation loading state
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    // Collection assignment picker: which document's picker is open
    const [assignPickerDocId, setAssignPickerDocId] = useState<string | null>(null);

    // Bulk assign picker: show collection dropdown when bulk-assigning selected docs
    const [showBulkAssignPicker, setShowBulkAssignPicker] = useState<boolean>(false);
    
    // Drag and drop state
    const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);
    const [isProcessingFolders, setIsProcessingFolders] = useState<boolean>(false);
    
    // Drag and Drop confirmation modal state
    const [showDragDropModal, setShowDragDropModal] = useState<boolean>(false);
    const [pendingDroppedFiles, setPendingDroppedFiles] = useState<File[]>([]);
    const [pendingDroppedFolders, setPendingDroppedFolders] = useState<string[]>([]);

    // Magazine export state
    const [isExportingMagazine, setIsExportingMagazine] = useState<boolean>(false);
    const encryptExport = useChatManagementStore((s) => s.export.encryptExport);
    const signExport = useChatManagementStore((s) => s.export.signExport);
    const { setEncryptExport, setSignExport } = useChatManagementStore((s) => s.actions);

    // Reset encrypt/sign on mount to preserve always-fresh-on-open behavior
    useEffect(() => {
        setEncryptExport(false);
        setSignExport(false);
    }, [setEncryptExport, setSignExport]);

    // Key availability: hide encrypt/sign when no key is configured
    const backendOptions = useBackendOptions();
    const hasEncryptKey = backendOptions
        ? Boolean(backendOptions.helpers.getValue('mag_encrypt_key'))
        : false;
    const hasSigningKey = backendOptions
        ? Boolean(backendOptions.helpers.getValue('mag_signing_key'))
        : false;

    const magazineTooltip = (() => {
        const base = '.mag - portable archive for documents and chats';
        if (!backendOptions || (hasEncryptKey && hasSigningKey)) return base;
        let capability = 'encryption and signing';
        if (hasSigningKey) capability = 'encryption';
        else if (hasEncryptKey) capability = 'signing';
        return `${base}. Configure ${capability} in settings`;
    })();

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const nameInputRef = useRef<HTMLInputElement>(null);
    const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const closeConfirmButtonRef = useRef<HTMLButtonElement>(null);
    const deleteConfirmButtonRef = useRef<HTMLButtonElement>(null);
    const createNameInputRef = useRef<HTMLInputElement>(null);
    const createDescriptionTextareaRef = useRef<HTMLTextAreaElement>(null);
    const cancellationRef = useRef(false);


    const loadCollections = useCallback(async () => {
        try {
            setLoading(true);
            const collectionsData = await apiClient.collections.getCollections();
            
            if ('error' in collectionsData) {
                showToast('Failed to load collections', 'error', { timeout: 3000, placement: 'chat' });
                // Close the modal since we couldn't load collections
                toggleModal();
                return;
            }

            // Get total document count for "All" collection
            let allDocumentsCount = 0;
            try {
                // Try to get documents from the -1 collection first (consistent with new approach)
                const allCollectionFiles = await apiClient.collections.getFilesInCollection('-1');
                if ('error' in allCollectionFiles) {
                    // Fallback to the old method if -1 collection doesn't exist yet
                    console.log('All collection (-1) not found, using fallback count method');
                    const allFiles = await apiClient.files.listFiles();
                    allDocumentsCount = Array.isArray(allFiles) ? allFiles.length : 0;
                } else {
                    allDocumentsCount = allCollectionFiles.length;
                }
            } catch (error) {
                console.error('Error loading all documents count:', error);
                allDocumentsCount = 0;
            }

            // Create the "All" collection (always first)
            const allCollection: CollectionTile = {
                id: '-1',
                name: 'All',
                description: 'Contains all documents in your library',
                order: -1,
                documentCount: allDocumentsCount
            };

            // Get document counts and file lists for each user collection
            const collectionsWithCounts = await Promise.all(
                collectionsData.map(async (collection) => {
                    try {
                        const files = await apiClient.collections.getFilesInCollection(collection.id);
                        if ('error' in files) return { ...collection, documentCount: 0, files: [] };
                        return { ...collection, documentCount: files.length, files };
                    } catch (error) {
                        console.error(`Error loading files for collection ${collection.id}:`, error); // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
                        return { ...collection, documentCount: 0, files: [] };
                    }
                })
            );

            // Combine All collection with user collections (All collection first)
            setCollections([allCollection, ...collectionsWithCounts]);
        } catch (error) {
            console.error('Error loading collections:', error);
            showToast('Failed to load collections', 'error', { timeout: 3000, placement: 'chat' });
            // Close the modal since we couldn't load collections
            toggleModal();
        } finally {
            setLoading(false);
        }
    }, [toggleModal, showToast]);

    // Load collections from backend on component mount
    useEffect(() => {
        loadCollections();
    }, [loadCollections]);

    const { importMagazines, importProgress } = useMagazineImport({ adaptToModal: true, onComplete: loadCollections });

    const loadDocumentsForCollection = async (collectionId: string) => {
        try {
            if (collectionId === '-1') {
                // For "All" collection, load all documents using the same endpoint as other collections
                // Since we now ensure all files are in the -1 collection, this should work consistently
                const files = await apiClient.collections.getFilesInCollection('-1');
                if ('error' in files) {
                    // Fallback to rag.listFiles if the -1 collection doesn't exist yet
                    console.log('All collection (-1) not found in backend, using fallback method');
                    const allFiles = await apiClient.files.listFiles();
                    if (Array.isArray(allFiles)) {
                        setDocuments(allFiles);
                        setOriginalDocuments([...allFiles]);
                    } else {
                        console.error('Error loading all files via fallback:', allFiles);
                        setDocuments([]);
                    }
                } else {
                    setDocuments(files);
                    setOriginalDocuments([...files]);
                }
            } else {
                // Regular collection - load collection-specific documents
                const files = await apiClient.collections.getFilesInCollection(collectionId);
                if ('error' in files) {
                    console.error('Error loading collection files:', files.error);
                    setDocuments([]);
                } else {
                    setDocuments(files);
                    setOriginalDocuments([...files]);
                }
            }
        } catch (error) {
            console.error('Error loading collection documents:', error);
            setDocuments([]);
        }
    };

    // Centralized function to handle all state updates after document operations
    const updateModalState = useCallback((options: {
        operation: 'add' | 'remove' | 'delete' | 'refresh';
        documentIds?: string[];
        count?: number;
        targetCollectionId?: string;
        shouldRefreshServer?: boolean;
        shouldResetEditingState?: boolean;
    }) => {
        const {
            operation,
            documentIds = [],
            count = documentIds.length,
            targetCollectionId = selectedCollection?.id,
            shouldRefreshServer = true,
            shouldResetEditingState = false
        } = options;

        // 1. Update local documents state
        if (documentIds.length > 0 && (operation === 'remove' || operation === 'delete')) {
            setDocuments(prev => prev.filter(doc => !documentIds.includes(doc.id)));
            setDocumentsToRemove(prev => prev.filter(id => !documentIds.includes(id)));
        }

        // 2. Update collection counts
        if (operation !== 'refresh' && count > 0 && targetCollectionId) {
            setCollections(prev => prev.map(col => {
                let shouldUpdate = false;
                
                switch (operation) {
                    case 'add':
                        // For add operations, update the target collection and "All" collection
                        shouldUpdate = col.id === targetCollectionId || 
                                      (col.id === '-1' && targetCollectionId !== '-1');
                        if (shouldUpdate) {
                            return { ...col, documentCount: (col.documentCount || 0) + count };
                        }
                        break;
                        
                    case 'remove':
                        // For remove operations, only update the target collection
                        shouldUpdate = col.id === targetCollectionId;
                        if (shouldUpdate) {
                            return { ...col, documentCount: Math.max(0, (col.documentCount || 0) - count) };
                        }
                        break;
                        
                    case 'delete':
                        // For delete operations, update target collection and "All" collection
                        shouldUpdate = col.id === targetCollectionId || (col.id === '-1');
                        if (shouldUpdate) {
                            return { ...col, documentCount: Math.max(0, (col.documentCount || 0) - count) };
                        }
                        break;
                }
                
                return col;
            }));
        }

        // 3. Reset editing state if requested
        if (shouldResetEditingState) {
            setDocumentsToRemove([]);
            setAddedFiles(new Set());
            setAddedFileDetails(new Map());
            setEditingName(false);
            setEditingDescription(false);
            setHasChanges(false);
        }

        // 4. Update DocumentContext for immediate sync with Documents.tsx
        if (operation !== 'refresh') {
            if (operation === 'add') {
                // For add operations, we need to refresh server data since we don't have the document objects
                updateDocumentContext({
                    operation: 'refresh',
                    shouldRefreshServer: true
                });
            } else if (operation === 'remove') {
                // For remove (disassociate) operations, force a refresh so 'All' remains accurate
                updateDocumentContext({
                    operation: 'refresh',
                    shouldRefreshServer: true
                });
            } else if (documentIds.length > 0) {
                // For delete operations, we have the document IDs
                updateDocumentContext({
                    operation,
                    documentIds,
                    targetCollectionId,
                    shouldRefreshServer: false // We'll handle server refresh separately
                });
            }
        }

        // 5. Refresh server data if requested
        if (shouldRefreshServer) {
            loadCollections();
            fetchDocuments();
        }
    }, [selectedCollection, loadCollections, fetchDocuments, updateDocumentContext]);

    // Function to check scroll position and update arrow visibility
    const checkScrollPosition = useCallback(() => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            
            // Show left arrow if we can scroll left (with small tolerance)
            setShowLeftArrow(scrollLeft > 5);
            
            // Show right arrow if we can scroll right (with larger tolerance for browser differences)
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
        }
    }, []);

    // Function to handle scrolling the collections bar
    const handleScroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const scrollAmount = 280; // Slightly larger than card width (256px) + gap
            const currentScrollLeft = scrollContainerRef.current.scrollLeft;
            const newScrollLeft = direction === 'left' 
                ? Math.max(0, currentScrollLeft - scrollAmount)
                : currentScrollLeft + scrollAmount;
            
            scrollContainerRef.current.scrollTo({
                left: newScrollLeft,
                behavior: 'smooth'
            });
            
            // Check scroll position after animation completes
            setTimeout(() => {
                checkScrollPosition();
            }, 300);
        }
    };

    // Check scroll position when collections change or component mounts
    useEffect(() => {
        // Add a small delay to ensure the container is fully rendered
        const timer = setTimeout(() => {
            checkScrollPosition();
        }, 100);
        
        return () => clearTimeout(timer);
    }, [collections, checkScrollPosition]);

    // Add scroll event listener to update arrows in real-time
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            // Add both scroll and wheel event listeners for better responsiveness
            scrollContainer.addEventListener('scroll', checkScrollPosition);
            scrollContainer.addEventListener('wheel', checkScrollPosition);
            // Also check on resize
            window.addEventListener('resize', checkScrollPosition);
            
            return () => {
                scrollContainer.removeEventListener('scroll', checkScrollPosition);
                scrollContainer.removeEventListener('wheel', checkScrollPosition);
                window.removeEventListener('resize', checkScrollPosition);
            };
        }
    }, [checkScrollPosition]);

    // Select the first collection by default
    useEffect(() => {
        if (collections.length > 0 && !selectedCollection) {
            setSelectedCollection(collections[0]);
        }
    }, [collections, selectedCollection]);

    // Re-sync selectedCollection when the store refreshes (e.g. after magazine import)
    useEffect(() => {
        if (!selectedCollection) return;
        const updated = collections.find((c) => c.id === selectedCollection.id);
        if (updated && updated !== selectedCollection) setSelectedCollection(updated);
    }, [collections, selectedCollection]);

    // Only administrators can publish a collection to the whole instance. The
    // backend rejects it either way; this keeps the control from appearing to
    // everyone else as something they can use.
    useEffect(() => {
        let cancelled = false;
        apiClient.auth
            .getCurrentUser()
            .then((u) => {
                if (!cancelled) {
                    setIsAdmin(!!u?.is_superuser);
                    setIsPiiPhiEligible(!!u?.pii_phi_eligible);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setIsAdmin(false);
                    setIsPiiPhiEligible(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Collections reached through a share are read-only to the recipient, and
    // the API omits is_owner only on older backends — treat absent as owned so
    // this degrades to the previous behaviour rather than hiding the controls.
    const isOwnedCollection = selectedCollection?.is_owner !== false;
    const isCollectionController = Boolean(selectedCollection?.is_pii_phi_controller);
    const canManageCollection = isOwnedCollection || isCollectionController;

    // Who a collection is shared with is only visible to its owner, so a
    // non-owner simply sees no list rather than an error.
    const loadShares = useCallback(async (collectionId: string) => {
        if (!collectionId || collectionId === '-1') {
            setShares([]);
            return;
        }
        setSharesLoading(true);
        try {
            const [current, candidates] = await Promise.all([
                apiClient.collections.getCollectionShares(collectionId),
                apiClient.collections.getShareableUsers().catch(() => [] as ShareableUser[]),
            ]);
            setShares(current);
            setShareableUsers(candidates);
        } catch {
            setShares([]);
        } finally {
            setSharesLoading(false);
        }
    }, []);

    useEffect(() => {
        setShareUserId('');
        setSharePermission('view');
        if (selectedCollection && (selectedCollection.is_owner !== false || selectedCollection.is_pii_phi_controller)) {
            void loadShares(selectedCollection.id);
        }
        else setShares([]);
    }, [selectedCollection, loadShares]);

    const handleShare = useCallback(async () => {
        if (!selectedCollection || !shareUserId) return;
        try {
            await apiClient.collections.shareCollection(
                selectedCollection.id,
                shareUserId,
                sharePermission
            );
            setShareUserId('');
            await loadShares(selectedCollection.id);
            showToast('Collection shared', 'success');
        } catch (error) {
            showToast(
                error instanceof Error ? error.message : 'Could not share collection',
                'error'
            );
        }
    }, [selectedCollection, shareUserId, sharePermission, loadShares, showToast]);

    const handleUnshare = useCallback(
        async (userId: string) => {
            if (!selectedCollection) return;
            try {
                await apiClient.collections.unshareCollection(selectedCollection.id, userId);
                await loadShares(selectedCollection.id);
                showToast('Access revoked', 'success');
            } catch (error) {
                showToast(
                    error instanceof Error ? error.message : 'Could not revoke access',
                    'error'
                );
            }
        },
        [selectedCollection, loadShares, showToast]
    );

    // Load documents when selected collection changes
    useEffect(() => {
        if (selectedCollection) {
            setTempName(selectedCollection.name);
            setTempDescription(selectedCollection.description);
            setTempIsShared(Boolean(selectedCollection.is_shared));
            
            setDocumentsToRemove([]);
            
            // Don't clear added files if we're currently uploading
            // This preserves provisionally selected files when upload is cancelled
            if (!uploadProgress.isUploading) {
                setAddedFiles(new Set());
                setAddedFileDetails(new Map());
            }
            
            // Set hasChanges to false when switching collections
            setHasChanges(false);
            
            // Exit editing modes when switching collections
            setEditingName(false);
            setEditingDescription(false);
            
            loadDocumentsForCollection(selectedCollection.id);
        }
    }, [selectedCollection, uploadProgress.isUploading]);

    // Focus input when editing starts
    useEffect(() => {
        if (editingName && nameInputRef.current) {
            nameInputRef.current.focus();
        }
    }, [editingName]);

    useEffect(() => {
        if (editingDescription && descriptionTextareaRef.current) {
            descriptionTextareaRef.current.focus();
        }
    }, [editingDescription]);

    // Focus the close button in the confirmation dialog when it appears
    useEffect(() => {
        if (showConfirmation && closeConfirmButtonRef.current) {
            setTimeout(() => {
                closeConfirmButtonRef.current?.focus();
            }, 100);
        }
    }, [showConfirmation]);

    // Focus the delete confirm button when delete confirmation appears
    useEffect(() => {
        if (showDeleteConfirmation && deleteConfirmButtonRef.current) {
            setTimeout(() => {
                deleteConfirmButtonRef.current?.focus();
            }, 100);
        }
    }, [showDeleteConfirmation]);

    // Check if there are any changes from the original values
    useEffect(() => {
        if (selectedCollection) {
            const nameChanged = tempName !== selectedCollection.name;
            const descriptionChanged = tempDescription !== selectedCollection.description;
            const sharedChanged = tempIsShared !== Boolean(selectedCollection.is_shared);
            const documentsChanged = documentsToRemove.length > 0;
            const filesAdded = addedFiles.size > 0;
            
            setHasChanges(nameChanged || descriptionChanged || sharedChanged || documentsChanged || filesAdded);
        }
    }, [tempName, tempDescription, tempIsShared, selectedCollection, documentsToRemove, addedFiles]);

    // Add escape key handler for the modal and confirmation dialog
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (showCreateModal) {
                    setShowCreateModal(false);
                    setNewCollectionName('');
                    setNewCollectionDescription('');
                } else if (showDeleteCollectionConfirmation) {
                    setShowDeleteCollectionConfirmation(false);
                    setCollectionToDelete(null);
                } else if (showDeleteConfirmation) {
                    setShowDeleteConfirmation(false);
                } else if (showConfirmation) {
                    setShowConfirmation(false);
                    setTempCollection(null);
                } else if (contextMenuOpen) {
                    closeContextMenu();
                } else if (hasChanges) {
                    setTempCollection(null);
                    setShowConfirmation(true);
                } else {
                    toggleModal();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown as any);
        return () => {
            document.removeEventListener('keydown', handleKeyDown as any);
        };
    }, [hasChanges, showConfirmation, showDeleteConfirmation, showDeleteCollectionConfirmation, showCreateModal, contextMenuOpen, toggleModal, closeContextMenu]);

    // Function to handle removing a document from collection (unassign only - file remains in system)
    const handleRemoveDocument = useCallback(async (documentId: string) => {
        if (selectedCollection) {
            // Prevent removing documents from the "All" collection
            if (selectedCollection.id === '-1') {
                showToast('Documents cannot be removed from the "All" collection. Use permanent delete instead.', 'error', { timeout: 3000, placement: 'chat' });
                return;
            }
            
            try {
                // This only removes the file from this specific collection
                // The file remains in the system and will still appear in "All" collection
                const result = await apiClient.collections.removeFileFromCollection(selectedCollection.id, documentId);
                
                if (!result.success) {
                    console.error('Error removing document from collection');
                    showToast('Failed to remove document from collection', 'error', { timeout: 3000, placement: 'chat' });
                } else {
                    // Update all modal state using centralized function
                    updateModalState({
                        operation: 'remove',
                        documentIds: [documentId],
                        targetCollectionId: selectedCollection.id
                    });
                    
                    showToast('Document removed from collection', 'success', { timeout: 3000, placement: 'chat' });
                }
            } catch (error) {
                console.error('Error removing document from collection:', error);
                showToast('Failed to remove document from collection', 'error', { timeout: 3000, placement: 'chat' });
            }
        }
    }, [selectedCollection, showToast, updateModalState]);

    // Function to initiate permanent document deletion
    const handleDeleteDocument = useCallback((document: FileInfo) => {
        if (selectedCollection?.is_owner === false) {
            showToast('Shared collections are read-only', 'error', { timeout: 3000, placement: 'chat' });
            return;
        }
        setDocumentToDelete(document);
        setShowDeleteConfirmation(true);
    }, [selectedCollection, showToast]);
    
    // Function to confirm permanent deletion (removes file from entire system)
    const handleConfirmDelete = useCallback(async () => {
        if (documentToDelete) {
            try {
                setIsDeleting(true);
                
                // Permanently delete by removing from the "All" collection (-1)
                // This will cause the backend to delete the file from the entire system
                const result = await apiClient.collections.removeFileFromCollection('-1', documentToDelete.id);
                
                if (!result.success) {
                    console.error('Error deleting document');
                    showToast('Failed to delete document', 'error', { timeout: 3000, placement: 'chat' });
                } else {
                    console.log("Document permanently deleted:", documentToDelete.id);
                    
                    // Update all modal state using centralized function
                    updateModalState({
                        operation: 'delete',
                        documentIds: [documentToDelete.id],
                        targetCollectionId: selectedCollection?.id
                    });
                    
                    showToast('Document deleted successfully', 'success', { timeout: 3000, placement: 'chat' });
                }
            } catch (error) {
                console.error('Error deleting document:', error);
                showToast('Failed to delete document', 'error', { timeout: 3000, placement: 'chat' });
            } finally {
                setIsDeleting(false);
            }
        }
        setShowDeleteConfirmation(false);
        setDocumentToDelete(null);
    }, [documentToDelete, showToast, updateModalState, selectedCollection]);

    // Filter out documents that are marked for removal
    const visibleDocuments = documents.filter(doc => 
        !documentsToRemove.includes(doc.id)
    );

    const handleNameEdit = useCallback(() => {
        if (selectedCollection?.is_owner === false) {
            showToast('Shared collections are read-only', 'error', { timeout: 3000, placement: 'chat' });
            return;
        }
        // Prevent editing of the "All" collection
        if (selectedCollection?.id === '-1') {
            showToast('The "All" collection cannot be edited', 'error', { timeout: 3000, placement: 'chat' });
            return;
        }
       setEditingName(true);
    }, [selectedCollection, showToast]);

    const handleDescriptionEdit = useCallback(() => {
        if (selectedCollection?.is_owner === false) {
            showToast('Shared collections are read-only', 'error', { timeout: 3000, placement: 'chat' });
            return;
        }
        // Prevent editing of the "All" collection
        if (selectedCollection?.id === '-1') {
            showToast('The "All" collection cannot be edited', 'error', { timeout: 3000, placement: 'chat' });
            return;
        }
        setEditingDescription(true);
    }, [selectedCollection, showToast]);

    const handleApplyChanges = useCallback(async () => {
        if (selectedCollection?.is_owner === false && !selectedCollection.is_pii_phi_controller) {
            showToast('Shared collections are read-only', 'error', { timeout: 3000, placement: 'chat' });
            return;
        }
        if (selectedCollection && hasChanges) {
            try {
                setLoading(true);
                
                // Validate input lengths before updating
                if (tempName.trim().length > MAX_COLLECTION_NAME_LENGTH) {
                    showToast(`Collection name must be ${MAX_COLLECTION_NAME_LENGTH} characters or less`, 'error', { timeout: 3000, placement: 'chat' });
                    return;
                }

                if (tempDescription.length > MAX_COLLECTION_DESCRIPTION_LENGTH) {
                    showToast(`Collection description must be ${MAX_COLLECTION_DESCRIPTION_LENGTH} characters or less`, 'error', { timeout: 3000, placement: 'chat' });
                    return;
                }

                // Update collection details if name or description changed
                const nameChanged = tempName !== selectedCollection.name;
                const descriptionChanged = tempDescription !== selectedCollection.description;
                const sharedChanged = tempIsShared !== Boolean(selectedCollection.is_shared);
                
                if (nameChanged || descriptionChanged || sharedChanged) {
                    const updateRequest = {
                        id: selectedCollection.id,
                        name: tempName,
                        description: tempDescription,
                        order: selectedCollection.order,
                        is_shared: tempIsShared,
                        pii_phi_controlled: Boolean(selectedCollection.pii_phi_controlled)
                    };
                    
                    const result = await apiClient.collections.updateCollectionById(selectedCollection.id, updateRequest);
                    if ('error' in result) {
                        console.error('Error updating collection:', result.error);
                        showToast('Failed to update collection', 'error', { timeout: 3000, placement: 'chat' });
                        return;
                    }
                    
                    // Update the selected collection with the new values
                    setSelectedCollection(prev => prev ? {
                        ...prev,
                        name: tempName,
                        description: tempDescription,
                        is_shared: tempIsShared
                    } : null);
                    
                    // Update the collections array to reflect the changes in the UI
                    setCollections(prev => prev.map(col => 
                        col.id === selectedCollection.id 
                            ? { ...col, name: tempName, description: tempDescription, is_shared: tempIsShared }
                            : col
                    ));
                    
                    // Sync with other components about the collection metadata change
                    updateDocumentContext({
                        operation: 'refresh',
                        shouldRefreshServer: true // Need to refresh to get updated collection data
                    });
                }
                
                // Remove documents from collection
                if (documentsToRemove.length > 0) {
                    for (const documentId of documentsToRemove) {
                        try {
                            const result = await apiClient.collections.removeFileFromCollection(selectedCollection.id, documentId);
                            if (!result.success) {
                                console.error('Error removing document from collection');
                            }
                        } catch (error) {
                            console.error('Error removing document from collection:', error);
                        }
                    }
                    
                    setDocuments(prev => prev.filter(doc => !documentsToRemove.includes(doc.id)));
                    setDocumentsToRemove([]);
                }
                
                // Add new files to collection using the hook
                if (addedFiles.size > 0) {
                    const filesToUpload: FileDetails[] = Array.from(addedFileDetails.values());
                    
                    await uploadFiles(filesToUpload, {
                        collectionId: selectedCollection.id,
                        purpose: 'assistants',
                        onComplete: (successCount, errorCount) => {
                            // Show final result
                            if (successCount > 0 && errorCount === 0) {
                                showToast(selectedCollection.id === '-1' 
                                    ? `Successfully added ${successCount} file(s) to your library`
                                    : `Successfully added ${successCount} file(s) to collection`, 'success', { timeout: 3000, placement: 'chat' });
                            } else if (successCount > 0 && errorCount > 0) {
                                showToast(`Added ${successCount} file(s), ${errorCount} failed`, 'warning', { timeout: 3000, placement: 'chat' });
                            } else {
                                showToast('Failed to add files', 'error', { timeout: 3000, placement: 'chat' });
                            }
                            
                            // Update all modal state using centralized function
                            if (successCount > 0) {
                                updateModalState({
                                    operation: 'add',
                                    count: successCount,
                                    targetCollectionId: selectedCollection.id,
                                    shouldResetEditingState: true
                                });
                            } else {
                                // Reset editing state even if no files were uploaded
                                updateModalState({
                                    operation: 'refresh',
                                    shouldResetEditingState: true,
                                    shouldRefreshServer: false
                                });
                            }
                        }
                    });
                } else {
                    // No files to upload, just reload data and reset state
                    updateModalState({
                        operation: 'refresh',
                        shouldResetEditingState: true
                    });
                }
                
            } catch (error) {
                console.error('Error applying changes:', error);
                showToast('Failed to apply changes', 'error', { timeout: 3000, placement: 'chat' });
            } finally {
                setLoading(false);
            }
        }
    }, [selectedCollection, updateDocumentContext, hasChanges, tempName, tempDescription, tempIsShared, documentsToRemove, addedFiles, addedFileDetails, showToast, uploadFiles, updateModalState]);

    const handleCancelChanges = useCallback(() => {
        if (selectedCollection) {
            setTempName(selectedCollection.name);
            setTempDescription(selectedCollection.description);
            setTempIsShared(Boolean(selectedCollection.is_shared));
            setDocumentsToRemove([]);
            setAddedFiles(new Set());
            setAddedFileDetails(new Map());
            setEditingName(false);
            setEditingDescription(false);
            setHasChanges(false);
        }
    }, [selectedCollection]);

    const handleCloseModal = useCallback(() => {
        if (hasChanges) {
            setTempCollection(null);
            setShowConfirmation(true);
        } else {
            toggleModal();
        }
    }, [hasChanges, toggleModal]);

    const handleConfirmClose = useCallback(() => {
        if (tempCollection) {
            setSelectedCollection(tempCollection);
            setTempCollection(null);
            setShowConfirmation(false);
        } else {
            toggleModal();
        }
    }, [toggleModal, tempCollection]);

    const handleCancelClose = useCallback(() => {
        setShowConfirmation(false);
        setTempCollection(null);
    }, []);
    
    const handleCancelDeleteConfirmation = useCallback(() => {
        setShowDeleteConfirmation(false);
        setDocumentToDelete(null);
    }, []);

    // Create collection handlers
    const handleOpenCreateModal = useCallback(() => {
        setShowCreateModal(true);
        setNewCollectionName('');
        setNewCollectionDescription('');
        setNewCollectionIsShared(false);
        setNewCollectionIsPiiPhiControlled(false);
    }, []);

    const handleCloseCreateModal = useCallback(() => {
        setShowCreateModal(false);
        setNewCollectionName('');
        setNewCollectionDescription('');
        setNewCollectionIsShared(false);
        setNewCollectionIsPiiPhiControlled(false);
    }, []);

    const handleCreateCollection = useCallback(async () => {
        if (!newCollectionName.trim()) {
            showToast('Collection name is required', 'error', { timeout: 3000, placement: 'chat' });
            return;
        }

        // Check name length
        if (newCollectionName.trim().length > MAX_COLLECTION_NAME_LENGTH) {
            showToast(`Collection name must be ${MAX_COLLECTION_NAME_LENGTH} characters or less`, 'error', { timeout: 3000, placement: 'chat' });
            return;
        }

        // Check description length
        if (newCollectionDescription.length > MAX_COLLECTION_DESCRIPTION_LENGTH) {
            showToast(`Collection description must be ${MAX_COLLECTION_DESCRIPTION_LENGTH} characters or less`, 'error', { timeout: 3000, placement: 'chat' });
            return;
        }

        // Prevent creating a collection named "All"
        if (newCollectionName.trim().toLowerCase() === 'all') {
            showToast('Collection name "All" is reserved. Please choose a different name.', 'error', { timeout: 3000, placement: 'chat' });
            return;
        }

        try {
            setCreatingCollection(true);
            
            // Generate a unique ID for the new collection
            const newId = generateCollectionId();

            const createRequest = {
                id: newId,
                name: newCollectionName.trim(),
                description: newCollectionDescription.trim(),
                order: collections.length, // Add to the end
                is_shared: newCollectionIsPiiPhiControlled ? false : newCollectionIsShared,
                pii_phi_controlled: newCollectionIsPiiPhiControlled
            };

            const result = await apiClient.collections.createCollection(createRequest);
            
            if ('error' in result) {
                showToast('Failed to create collection', 'error', { timeout: 3000, placement: 'chat' });
                return;
            }

            // Add the new collection to the list with document count 0
            const newCollection: CollectionTile = {
                ...result,
                documentCount: 0
            };

            setCollections(prev => [...prev, newCollection]);
            setSelectedCollection(newCollection);
            
            // Sync with DocumentContext to update other components about the new collection
            updateDocumentContext({
                operation: 'refresh',
                shouldRefreshServer: true
            });
            
            showToast('Collection created successfully', 'success', { timeout: 3000, placement: 'chat' });

            handleCloseCreateModal();
            
        } catch (error) {
            console.error('Error creating collection:', error);
            showToast('Failed to create collection', 'error', { timeout: 3000, placement: 'chat' });
        } finally {
            setCreatingCollection(false);
        }
    }, [newCollectionName, updateDocumentContext, newCollectionDescription, newCollectionIsShared, newCollectionIsPiiPhiControlled, collections.length, showToast, handleCloseCreateModal]);

    // Focus management for create modal
    useEffect(() => {
        if (showCreateModal && createNameInputRef.current) {
            setTimeout(() => {
                createNameInputRef.current?.focus();
            }, 100);
        }
    }, [showCreateModal]);

    // Collection deletion handlers
    const handleDeleteCollection = useCallback((collection: CollectionTile) => {
        if (collection.is_owner === false) {
            showToast('Only the collection owner can delete it', 'error', { timeout: 3000, placement: 'chat' });
            closeContextMenu();
            return;
        }
        // Prevent deletion of the "All" collection
        if (collection.id === '-1') {
            showToast('The "All" collection cannot be deleted', 'error', { timeout: 3000, placement: 'chat' });
            closeContextMenu();
            return;
        }
        
        setCollectionToDelete(collection);
        setShowDeleteCollectionConfirmation(true);
        closeContextMenu();
    }, [closeContextMenu, showToast]);

    const handleConfirmDeleteCollection = useCallback(async () => {
        if (collectionToDelete) {
            try {
                const result = await apiClient.collections.deleteCollectionById(collectionToDelete.id);
                if (!result.success) {
                    showToast('Failed to delete collection', 'error', { timeout: 3000, placement: 'chat' });
                } else {
                    // Remove collection from local state
                    setCollections(prev => prev.filter(col => col.id !== collectionToDelete.id));
                    
                    // If the deleted collection was selected, select the first remaining one
                    if (selectedCollection?.id === collectionToDelete.id) {
                        const remainingCollections = collections.filter(col => col.id !== collectionToDelete.id);
                        setSelectedCollection(remainingCollections.length > 0 ? remainingCollections[0] : null);
                    }
                    
                    // Update DocumentContext to refresh Documents.tsx with latest collections
                    updateDocumentContext({
                        operation: 'refresh',
                        shouldRefreshServer: true
                    });
                    
                    showToast('Collection deleted successfully', 'success', { timeout: 3000, placement: 'chat' });
                }
            } catch (error) {
                console.error('Error deleting collection:', error);
                showToast('Failed to delete collection', 'error', { timeout: 3000, placement: 'chat' });
            }
        }
        setShowDeleteCollectionConfirmation(false);
        setCollectionToDelete(null);
    }, [collectionToDelete, collections, selectedCollection, showToast, updateDocumentContext]);

    const handleCancelDeleteCollectionConfirmation = useCallback(() => {
        setShowDeleteCollectionConfirmation(false);
        setCollectionToDelete(null);
    }, []);

    // Handler for adding files from FileBrowser
    const handleAddFileFromBrowser = useCallback((file: FileNode) => {
        if (selectedCollection) {
            setAddedFiles(prev => new Set([...prev, file.path]));
            
            // In web mode, FileNode includes a 'file' property with the File object
            // In desktop mode, FileNode has a 'path' property
            const fileDetails: FileDetails = {
                name: file.name,
                path: file.path,
                file: (file as any).file // File object from web FileBrowser
            };
            
            setAddedFileDetails(prev => new Map([...prev, [file.path, fileDetails]]));
        }
    }, [selectedCollection]);

    // Bulk operations handlers
    const handleBulkRemove = useCallback(async () => {
        if (selectedCollection && selectedDocuments.length > 0) {
            // Prevent removing documents from the "All" collection
            if (selectedCollection.id === '-1') {
                showToast('Documents cannot be removed from the "All" collection. Use permanent delete instead.', 'error', { timeout: 3000, placement: 'chat' });
                return;
            }
            
            try {
                setLoading(true);
                
                for (const documentId of selectedDocuments) {
                    try {
                        const result = await apiClient.collections.removeFileFromCollection(selectedCollection.id, documentId);
                        if (!result.success) {
                            console.error('Error removing document from collection');
                        }
                    } catch (error) {
                        console.error('Error removing document from collection:', error);
                    }
                }
                
                // Update all modal state using centralized function
                updateModalState({
                    operation: 'remove',
                    documentIds: selectedDocuments,
                    count: selectedDocuments.length,
                    targetCollectionId: selectedCollection.id
                });
                
                // Clear selected documents after successful operation
                setSelectedDocuments([]);
                
                showToast(`Successfully removed ${selectedDocuments.length} file(s) from collection`, 'success', { timeout: 3000, placement: 'chat' });
                
            } catch (error) {
                console.error('Error in bulk remove:', error);
                showToast('Failed to remove files from collection', 'error', { timeout: 3000, placement: 'chat' });
            } finally {
                setLoading(false);
            }
        }
    }, [selectedCollection, selectedDocuments, showToast, updateModalState]);

    const handleBulkDelete = useCallback(async () => {
        if (selectedDocuments.length > 0) {
            try {
                setIsDeleting(true);
                let successCount = 0;
                let errorCount = 0;
                
                for (const documentId of selectedDocuments) {
                    try {
                        // Permanently delete by removing from the "All" collection (-1)
                        const result = await apiClient.collections.removeFileFromCollection('-1', documentId);
                        if (!result.success) {
                            console.error('Error deleting document');
                            errorCount++;
                        } else {
                            successCount++;
                        }
                    } catch (error) {
                        console.error('Error deleting document:', error);
                        errorCount++;
                    }
                }
                
                // Update all modal state using centralized function
                if (successCount > 0) {
                    updateModalState({
                        operation: 'delete',
                        documentIds: selectedDocuments,
                        count: successCount,
                        targetCollectionId: selectedCollection?.id
                    });
                }
                
                // Clear selected documents after successful operation
                setSelectedDocuments([]);
                
                // Show result
                if (successCount > 0 && errorCount === 0) {
                    showToast(`Successfully deleted ${successCount} file(s)`, 'success', { timeout: 3000, placement: 'chat' });
                } else if (successCount > 0 && errorCount > 0) {
                    showToast(`Deleted ${successCount} file(s), ${errorCount} failed`, 'warning', { timeout: 3000, placement: 'chat' });
                } else {
                    showToast('Failed to delete files', 'error', { timeout: 3000, placement: 'chat' });
                }
                
            } catch (error) {
                console.error('Error in bulk delete:', error);
                showToast('Failed to delete files', 'error', { timeout: 3000, placement: 'chat' });
            } finally {
                setIsDeleting(false);
            }
        }
    }, [selectedDocuments, showToast, updateModalState, selectedCollection]);

    const handleConfirmBulkOperation = useCallback(async () => {
        if (bulkOperation === 'remove') {
            await handleBulkRemove();
        } else if (bulkOperation === 'delete') {
            await handleBulkDelete();
        }
        setShowBulkDeleteConfirmation(false);
        setBulkOperation(null);
    }, [bulkOperation, handleBulkRemove, handleBulkDelete]);

    const handleCancelBulkOperation = useCallback(() => {
        setShowBulkDeleteConfirmation(false);
        setBulkOperation(null);
    }, []);

    const handleToggleDocumentSelection = useCallback((documentId: string) => {
        setSelectedDocuments(prev => {
            if (prev.includes(documentId)) {
                return prev.filter(id => id !== documentId);
            } 
                return [...prev, documentId];
            
        });
    }, []);

    const handleSelectAllDocuments = useCallback(() => {
        if (selectedDocuments.length === visibleDocuments.length) {
            // Deselect all
            setSelectedDocuments([]);
        } else {
            // Select all visible documents
            setSelectedDocuments(visibleDocuments.map(doc => doc.id));
        }
    }, [selectedDocuments.length, visibleDocuments]);

    // Toggle a document's membership in a collection directly (no save needed)
    const handleToggleDocInCollection = useCallback(async (fileId: string, collectionId: string, currentlyIn: boolean) => {
        try {
            if (currentlyIn) {
                const result = await apiClient.collections.removeFileFromCollection(collectionId, fileId);
                if (result.success) {
                    setCollections(prev => prev.map(c =>
                        c.id === collectionId
                            ? { ...c, files: (c.files || []).filter(f => f.id !== fileId), documentCount: Math.max(0, (c.documentCount || 0) - 1) }
                            : c
                    ));
                    showToast('Removed from collection', 'success', { timeout: 2000, placement: 'chat' });
                }
            } else {
                const result = await apiClient.collections.assignFileToCollection(collectionId, fileId);
                if (result.assigned) {
                    const fileObj = documents.find(d => d.id === fileId);
                    if (fileObj) {
                        setCollections(prev => prev.map(c =>
                            c.id === collectionId
                                ? { ...c, files: [...(c.files || []), fileObj], documentCount: (c.documentCount || 0) + 1 }
                                : c
                        ));
                    }
                    showToast('Added to collection', 'success', { timeout: 2000, placement: 'chat' });
                }
            }
        } catch (error) {
            showToast('Failed to update collection assignment', 'error', { timeout: 3000, placement: 'chat' });
        }
    }, [documents, showToast]);

    const handleBulkAssignToCollection = useCallback(async (targetCollectionId: string) => {
        if (selectedDocuments.length === 0) return;
        setShowBulkAssignPicker(false);
        let successCount = 0;
        for (const fileId of selectedDocuments) {
            try {
                const result = await apiClient.collections.assignFileToCollection(targetCollectionId, fileId);
                if (result.assigned) {
                    successCount++;
                    const fileObj = documents.find(d => d.id === fileId);
                    if (fileObj) {
                        setCollections(prev => prev.map(c =>
                            c.id === targetCollectionId
                                ? { ...c, files: (c.files || []).some(f => f.id === fileId) ? c.files : [...(c.files || []), fileObj], documentCount: (c.files || []).some(f => f.id === fileId) ? c.documentCount : (c.documentCount || 0) + 1 }
                                : c
                        ));
                    }
                }
            } catch {
                // continue on individual failure
            }
        }
        const targetName = collections.find(c => c.id === targetCollectionId)?.name ?? 'collection';
        if (successCount > 0) {
            showToast(`Added ${successCount} file(s) to "${targetName}"`, 'success', { timeout: 3000, placement: 'chat' });
        } else {
            showToast('No files were added', 'warning', { timeout: 3000, placement: 'chat' });
        }
    }, [selectedDocuments, documents, collections, showToast]);

    // Replace handleCancelUpload with hook's cancelUpload
    const handleCancelUpload = useCallback(async () => {
        cancellationRef.current = true;
        await cancelUpload();
        
        showToast('Upload cancelled. Files remain selected for retry.', 'warning', { timeout: 3000, placement: 'chat' });
    }, [cancelUpload, showToast]);

    // Clear selected documents and close pickers when collection changes
    useEffect(() => {
        setSelectedDocuments([]);
        setAssignPickerDocId(null);
        setShowBulkAssignPicker(false);
    }, [selectedCollection]);

    // Handle drag and drop for folder-based collection creation
    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isProcessingFolders) {
            setIsDraggingOver(true);
        }
    }, [isProcessingFolders]);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        // Only hide overlay if we're leaving the modal entirely
        if (e.currentTarget === e.target) {
            setIsDraggingOver(false);
        }
    }, []);

    const handleDrop = useCallback(async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(false);

        const items = Array.from(e.dataTransfer.items);
        if (items.length === 0) return;

        setIsProcessingFolders(true);

        try {
            // Group files by their folder structure
            const folderMap = new Map<string, File[]>();

            // Get all entries immediately to prevent DataTransferItem invalidation during async processing
            const entries = items
                .filter(item => item.kind === 'file')
                .map(item => item.webkitGetAsEntry())
                .filter((entry): entry is FileSystemEntry => entry !== null);

            // Process all entries
            for (const entry of entries) {
                await processEntry(entry, '', folderMap);
            }

            if (folderMap.size === 0) {
                showToast('Unsupported File(s)', 'error', { timeout: 3000, adaptToModal: true });
                // setIsProcessingFolders(false);
                setShowDragDropModal(true);
                return;
            }

            // Flatten files for the modal
            const allFiles = Array.from(folderMap.values()).flat();
            const folders = Array.from(folderMap.keys());
            
            setPendingDroppedFiles(allFiles);
            setPendingDroppedFolders(folders);
            setShowDragDropModal(true);
            

        } catch (error) {
            console.error('Error processing dropped folders:', error);
            showToast('Failed to process dropped folders', 'error', { timeout: 3000, adaptToModal: true });
            setIsProcessingFolders(false);
        }
    }, [showToast]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleExportCollectionAsMagazine = useCallback(
        async (collection: CollectionTile) => {
            setIsExportingMagazine(true);
            try {
                let collectionIds: string[];
                let fileIds: string[] = [];
                let filename: string;

                if (collection.id === '-1') {
                    // "All" collection: all file IDs only (not collections)
                    collectionIds = [];
                    fileIds = documents.map((d) => d.id);
                    filename = generateMagazineFilename('all_files');
                } else {
                    collectionIds = [collection.id];
                    filename = generateMagazineFilename('collection', {
                        collectionName: collection.name,
                        count: collection.documentCount,
                    });
                }

                const result = await apiClient.files.exportMagazine({
                    collection_ids: collectionIds,
                    file_ids: fileIds,
                    history_ids: [],
                    encryption: encryptExport,
                    signing: signExport,
                    stream: true,
                });
                downloadMagazine(result instanceof Blob ? result : result.b64_mag, filename);
                showToast('Collection exported as Magazine', 'success', {
                    timeout: 3000,
                    adaptToModal: true,
                });
            } catch (error) {
                const msg = error instanceof Error ? error.message : 'Export failed';
                showToast(msg, 'error', { timeout: 3000, adaptToModal: true });
            } finally {
                setIsExportingMagazine(false);
            }
        },
        [encryptExport, signExport, showToast, documents],
    );

    const handleDragDropConfirm = useCallback(async (result: DragAndDropResult) => {
        if (uploadProgress.isUploading) {
            showToast('Please wait for the current upload to complete', 'warning', { adaptToModal: true, timeout: 3000 });
            return;
        }
        if (importProgress.isImporting) {
            showToast('Please wait for the magazine import to complete', 'warning', { adaptToModal: true, timeout: 3000 });
            return;
        }

        setShowDragDropModal(false);
        cancellationRef.current = false;

        const { files, mode, targetCollectionId, newCollectionName: dragDropNewCollectionName } = result;

        // Magazine import uses a separate code path
        if (result.isMagazineImport) {
            await importMagazines(files);
            return;
        }

        try {
            setTotalFilesToUpload(files.length);
            setIsDragDropUpload(true);
            
            if (mode === 'existing' && targetCollectionId) {
                let uploadedCount = 0;
                let uploadErrorCount = 0;

                for (const file of files) {
                    if (cancellationRef.current) break;
                    try {
                        const base64Data = await new Promise<string>((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = () => {
                                const readerResult = reader.result as string;
                                const base64 = readerResult.replace(/^data:.*;base64,/, '');
                                resolve(base64);
                            };
                            reader.onerror = reject;
                            reader.readAsDataURL(file);
                        });

                        await uploadBase64File(base64Data, file.name, {
                            collectionId: targetCollectionId,
                            purpose: 'assistants',
                            onSuccess: () => {
                                uploadedCount++;
                                setCurrentFileIndex(current => current + 1);
                            },
                            onError: (error) => {
                                console.error(`Failed to upload file ${file.name}:`, error); // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
                                uploadErrorCount++;
                            }
                        });
                    } catch (error) {
                        console.error(`Error processing file ${file.name}:`, error); // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
                        uploadErrorCount++;
                    }
                }

                // Refresh collections and docs
                await loadCollections();
                updateDocumentContext({
                    operation: 'refresh',
                    shouldRefreshServer: true
                });

                if (uploadedCount > 0) {
                     showToast(`Successfully uploaded ${uploadedCount} files`, 'success', { timeout: 3000, adaptToModal: true });
                } else if (uploadErrorCount > 0) {
                     showToast(`Failed to upload files`, 'error', { timeout: 3000, adaptToModal: true });
                }

            } else {
                // Re-group files by their folder structure
                const folderMap = new Map<string, File[]>();

                if (mode === 'new' && dragDropNewCollectionName) {
                    folderMap.set(dragDropNewCollectionName, files);
                } else {
                    files.forEach(file => {
                        const path = (file as any).webkitRelativePath || file.name;
                        const parts = path.split('/');
                        const folderName = parts.length > 1 ? parts[0] : 'Dropped Files';
                        
                        if (!folderMap.has(folderName)) {
                            folderMap.set(folderName, []);
                        }
                        folderMap.get(folderName)?.push(file);
                    });
                }
                
                // Create collections for each folder and upload files
                let successCount = 0;
                let _processingIndex = 0;

                for (const [folderName, folderFiles] of folderMap.entries()) {
                    if (cancellationRef.current) break;
                    
                    try {
                        _processingIndex++;
                        // Generate a unique ID for the new collection
                        const newId = generateCollectionId();

                        const baseName = folderName.trim();
                        let newFolderName = baseName;
                        let counter = 1;

                        // create a OS style name if duplicate exists
                        while (collections.some(c => c.name.toLowerCase() === newFolderName.toLowerCase())) {
                            newFolderName = `${baseName} (${counter})`;
                            console.log(`Creating collection for folder: ${newFolderName}`);
                            counter++;
                        }
                                
                        // Create the collection with folder name
                        const collectionResult = await apiClient.collections.createCollection({
                            id: newId,
                            name: newFolderName,
                            description: ``,
                            order: collections.length + successCount,
                            is_shared: false
                        });

                        const newCollectionId = collectionResult.id;

                        // Convert files to base64 and upload
                        let uploadedCount = 0;
                        let uploadErrorCount = 0;

                        for (const file of folderFiles) {
                            if (cancellationRef.current) break;
                            try {
                                // Read file as base64
                                const base64Data = await new Promise<string>((resolve, reject) => {
                                    const reader = new FileReader();
                                    reader.onload = () => {
                                        const readerResult = reader.result as string;
                                        // Remove data URI prefix
                                        const base64 = readerResult.replace(/^data:.*;base64,/, '');
                                        resolve(base64);
                                    };
                                    reader.onerror = reject;
                                    reader.readAsDataURL(file);
                                });

                                // Upload using base64
                                await uploadBase64File(base64Data, file.name, {
                                    collectionId: newCollectionId,
                                    purpose: 'assistants',
                                    onSuccess: () => {
                                        uploadedCount++;
                                        setCurrentFileIndex(prev => prev + 1);
                                    },
                                    onError: (error) => {
                                        console.error(`Failed to upload file ${file.name}:`, error); // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
                                        uploadErrorCount++;
                                    }
                                });

                            } catch (error) {
                                console.error(`Error processing file ${file.name}:`, error); // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
                                uploadErrorCount++;
                            }
                        }

                        if (uploadedCount > 0) {
                            console.log(`Successfully uploaded ${uploadedCount} files to collection "${folderName}"`);
                            successCount++;
                        } else if (uploadErrorCount > 0) {
                            console.error(`Failed to upload files to collection "${folderName}"`);
                            showToast(`Failed to upload files to "${folderName}"`, 'error', { timeout: 3000, adaptToModal: true });
                        }

                    } catch (error) {
                        console.error(`Error processing folder ${folderName}:`, error); // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
                        showToast(`Error processing folder "${folderName}"`, 'error', { timeout: 3000, adaptToModal: true });
                    }
                }

                // Reload collections and show success message
                await loadCollections();
                // sync with DocumentContext
                updateDocumentContext({
                    operation: 'refresh',
                    shouldRefreshServer: true
                });
                
                if (successCount > 0) {
                    showToast(
                        `Successfully created ${successCount} collection(s) from dropped folder(s)`,
                        'success',
                        { timeout: 3000, adaptToModal: true }
                    );
                }
            }

        } catch (error) {
            console.error('Error processing dropped folders:', error);
            showToast('Failed to process dropped folders', 'error', { timeout: 3000, adaptToModal: true });
        } finally {
            setIsProcessingFolders(false);
            setPendingDroppedFiles([]);
            setPendingDroppedFolders([]);
            setCurrentFileIndex(1);
            setTotalFilesToUpload(0);
            setIsDragDropUpload(false);
        }
    }, [collections, updateDocumentContext, loadCollections, showToast, uploadBase64File, importMagazines, uploadProgress.isUploading, importProgress.isImporting]);

    const handleDragDropCancel = useCallback(() => {
        setShowDragDropModal(false);
        setPendingDroppedFiles([]);
        setPendingDroppedFolders([]);
        setIsProcessingFolders(false);
    }, []);

    // Recursive function to process file system entries
    async function processEntry(
        entry: FileSystemEntry,
        parentPath: string,
        folderMap: Map<string, File[]>
    ): Promise<void> {
        if (entry.isFile) {
            const fileEntry = entry as FileSystemFileEntry;
            return new Promise<void>((resolve) => {
                fileEntry.file((file) => {
                    // Check if file is supported
                    const ext = `.${file.name.split('.').pop()?.toLowerCase() || ''}`;
                    const SUPPORTED_EXTENSIONS = [
                        '.docx', '.doc', '.txt', '.log', '.ini', '.rtf', '.odt',
                        '.xls', '.xlsx', '.csv', '.pptx', '.pdf', '.epub',
                        '.htm', '.html', '.md', '.json', '.xml'
                    ];
                    const SUPPORTED_ARCHIVE_EXTENSIONS = ['.mag'];

                    if (SUPPORTED_EXTENSIONS.includes(ext) || SUPPORTED_ARCHIVE_EXTENSIONS.includes(ext)) {
                        // Determine the folder this file belongs to
                        const fullPath = parentPath ? `${parentPath}/${entry.name}` : entry.name;
                        const pathParts = fullPath.split('/');
                        
                        // Use the first folder in the path as the collection name
                        // If dropped directly without a folder, use "Dropped Files"
                        const folderName = pathParts.length > 1 ? pathParts[0] : 'Dropped Files';

                        // Add webkitRelativePath for compatibility with existing upload logic
                        Object.defineProperty(file, 'webkitRelativePath', {
                            value: fullPath,
                            writable: false
                        });

                        if (!folderMap.has(folderName)) {
                            folderMap.set(folderName, []);
                        }
                        folderMap.get(folderName)!.push(file);
                    }
                    resolve();
                });
            });
        } if (entry.isDirectory) {
            const dirEntry = entry as FileSystemDirectoryEntry;
            const dirReader = dirEntry.createReader();
            
            return new Promise<void>((resolve, reject) => {
                const readEntries = () => {
                    dirReader.readEntries(
                        async (entries) => {
                            if (entries.length === 0) {
                                resolve();
                                return;
                            }

                            const newPath = parentPath ? `${parentPath}/${entry.name}` : entry.name;
                            
                            for (const childEntry of entries) {
                                await processEntry(childEntry, newPath, folderMap);
                            }
                            
                            // Continue reading (directories may have more than 100 entries)
                            readEntries();
                        },
                        (error) => {
                            console.error('Error reading directory:', error);
                            reject(error);
                        }
                    );
                };
                readEntries();
            });
        }
    }

    if (loading && collections.length === 0) {
    return (
            <div className="fixed top-0 left-0 right-0 bottom-0 bg-neutral-700/50 flex justify-center items-center z-[1000]">
                <div className="bg-neutral-700 rounded-medium p-lg shadow-large border-2 border-neutral-400">
                    <div className="text-center text-neutral-100">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto mb-4" />
                        <p>Loading collections...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div 
            className="fixed top-0 left-0 right-0 bottom-0 bg-neutral-700/50 flex justify-center items-center z-[1000]"
        >
            <div 
                className="bg-neutral-700 p-lg pb-xs pr-0 rounded-medium w-[90%] max-w-[1400px] h-[85%] max-h-[800px] shadow-large border-2 border-neutral-400 flex flex-col box-border"
                ref={modalRef} 
                onClick={(e) => e.stopPropagation()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {/* Header */}
                <div className="flex justify-between items-center pb-xs pr-lg mb-xs">
                    <h2 className="m-0 text-lg text-neutral-100">Manage Collections</h2>
                    <button
                        onClick={handleCloseModal}
                        className={`bg-transparent border-0 text-xl transition-colors duration-200 ${
                            uploadProgress.isUploading || importProgress.isImporting
                                ? 'text-neutral-500 cursor-not-allowed'
                                : 'cursor-pointer text-neutral-100 hover:text-red-400'
                        }`}
                        aria-label="Close collections"
                        title={
                            uploadProgress.isUploading
                                ? "Cannot close while uploading"
                                : importProgress.isImporting
                                    ? "Cannot close while importing magazine"
                                    : "Close collections"
                        }
                        disabled={uploadProgress.isUploading || importProgress.isImporting}
                    >
                        <i className="fas fa-times" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 min-h-0 mb-xs text-neutral-100 flex flex-col overflow-hidden pr-lg">
                    {/* Collection Cards Section */}
                    <div className="mb-6 relative flex-shrink-0">
                        {/* Left scroll arrow */}
                        {showLeftArrow && !contextMenuOpen && (
                        <button 
                            onClick={() => handleScroll('left')}
                                className="absolute left-2 top-1/2 transform -translate-y-1/2 z-10 bg-neutral-600 hover:bg-neutral-500 text-neutral-200 hover:text-neutral-100 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 shadow-lg border border-neutral-400"
                            aria-label="Scroll left"
                        >
                                <i className="fas fa-chevron-left" />
                        </button>
                        )}
                        
                        {/* Right scroll arrow */}
                        {showRightArrow && !contextMenuOpen && (
                            <button
                                onClick={() => handleScroll('right')}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 z-10 bg-neutral-600 hover:bg-neutral-500 text-neutral-200 hover:text-neutral-100 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 shadow-lg border border-neutral-400"
                                aria-label="Scroll right"
                            >
                                <i className="fas fa-chevron-right" />
                            </button>
                        )}
                        
                        <div 
                            className={`flex gap-4 pb-4 scroll-smooth mx-12 ${!contextMenuOpen ? 'overflow-x-auto' : 'overflow-x-hidden pb-7'}`}
                            ref={scrollContainerRef}
                            style={{ 
                                scrollbarWidth: 'thin',
                                scrollBehavior: 'smooth'
                            }}
                            onScroll={checkScrollPosition}
                        >
                            {/* Create New Collection Button - First in list */}
                            <div
                                className="flex-shrink-0 w-44 bg-neutral-600 border-2 border-dashed border-neutral-400 rounded-medium p-3 cursor-pointer transition-all duration-200 hover:border-red-600 hover:bg-neutral-500 flex items-center justify-center group"
                                onClick={handleOpenCreateModal}
                            >
                                <div className="text-center text-neutral-300 group-hover:text-neutral-100 transition-colors duration-200">
                                    <div className="w-8 h-8 bg-neutral-500 rounded-full mx-auto mb-2 flex items-center justify-center group-hover:bg-red-600 transition-colors duration-200">
                                        <i className="fas fa-plus text-sm text-neutral-200" />
                                    </div>
                                    <h3 className="font-semibold text-xs mb-0.5">Create New</h3>
                                    <p className="text-xs opacity-70">Collection</p>
                                </div>
                            </div>
                            
                            {collections.map((collection, index) => (
                                <div
                                    key={collection.id}
                                    className={`flex-shrink-0 w-44 bg-neutral-500 border rounded-medium p-3 cursor-pointer transition-all duration-200 relative ${
                                        selectedCollection?.id === collection.id
                                            ? 'border-red-600 bg-neutral-400'
                                            : 'border-neutral-400 hover:border-neutral-300 hover:bg-neutral-400'
                                    } ${contextMenuOpen === `collection-${index}` ? 'bg-neutral-400' : ''}`}
                                    onClick={() => {
                                        if (hasChanges && selectedCollection?.id !== collection.id) {
                                            setShowConfirmation(true);
                                            setTempCollection(collection);
                                        } else {
                                            setSelectedCollection(collection);
                                        }
                                    }}
                                >
                                    {/* Collection Icon */}
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-start min-w-0 flex-1 mr-2">
                                            <i className="fas fa-folder text-neutral-200 mr-1.5 flex-shrink-0 mt-0.5 text-sm" />
                                            <div className="min-w-0 w-full">
                                                <h3 className="font-semibold text-neutral-100 text-xs break-all leading-tight min-w-0 w-full">{collection.name}</h3>
                                                {collection.id !== '-1' && (
                                                    <span className={`inline-flex mt-1 px-1.5 py-0 rounded-small text-[10px] font-medium ${
                                                        collection.is_shared
                                                            ? 'bg-red-700 text-neutral-100'
                                                            : 'bg-neutral-700 text-neutral-300'
                                                    }`}>
                                                        {collection.is_shared ? 'Public' : 'Private'}
                                                    </span>
                                                )}
                                            </div>
                                    </div>
                                        {/* Hide context menu for "All" collection */}
                                        {collection.id !== '-1' && (
                                            <button 
                                                className="bg-transparent border-0 cursor-pointer h-7 w-5 text-neutral-300 px-2 transition-colors duration-200 hover:text-red-600 active:text-red-600 flex-shrink-0"
                                                onClick={(e) => toggleContextMenu(`collection-${index}`, e)}
                                                aria-label="Open collection menu"
                                                title="Open collection menu"
                                            >
                                                <i className="fas fa-ellipsis-v text-lg" />
                                            </button>
                                        )}
                        </div>
                        
                                    {/* Description */}
                                    <p className="text-neutral-300 text-[11px] mb-1.5 line-clamp-1 leading-tight">
                                        {collection.description || <span className="italic opacity-50">No description</span>}
                                    </p>

                                    {/* Document Count */}
                                    <p className="text-neutral-400 text-[10px] font-medium">
                                        {collection.documentCount || 0} docs
                                    </p>

                                    {/* Context Menu */}
                                    {contextMenuOpen === `collection-${index}` && menuPosition && (
                                        <ContextMenuPortal x={menuPosition.x} y={menuPosition.y}>
                                            <div ref={menuRef} className="bg-neutral-600 border border-neutral-400 shadow-medium w-20 text-left rounded-large overflow-hidden">
                                                <button 
                                                    className="flex p-2 px-3 w-20 border-0 bg-transparent cursor-pointer text-left text-neutral-white hover:opacity-80"
                                                    onClick={handleMenuAction(() => {
                                                        if (hasChanges && selectedCollection?.id !== collection.id) {
                                                            setShowConfirmation(true);
                                                            setTempCollection(collection);
                                                        } else {
                                                            setSelectedCollection(collection);
                                                        }
                                                    })}
                                                    aria-label="Select collection"
                                                    title="Select collection"
                                                >
                                                    Select
                                                </button>
                                                <button
                                                    className="flex p-2 px-3 w-20 border-0 bg-transparent cursor-pointer text-left text-neutral-white hover:opacity-80"
                                                    onClick={handleMenuAction(() => handleExportCollectionAsMagazine(collection))}
                                                    aria-label="Export collection as Magazine"
                                                    title="Export collection as Magazine"
                                                >
                                                    Export
                                                </button>
                                                {collection.is_owner !== false && (
                                                    <button
                                                        className="flex p-2 px-3 w-20 border-0 bg-transparent cursor-pointer text-left text-neutral-white hover:opacity-80"
                                                        onClick={handleMenuAction(() => handleDeleteCollection(collection))}
                                                        aria-label="Delete collection"
                                                        title="Delete collection"
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </div>
                                        </ContextMenuPortal>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {/* Main Content Area */}
                    <div className="flex-1 flex gap-6 overflow-hidden">
                        {/* Left Panel - Collection Details */}
                        <div className="flex-1 flex flex-col overflow-hidden">
                            {selectedCollection && (
                                <>
                                    {/* Collection Info + Export - bounded height, scrolls internally if tall */}
                                    <div className="flex-none space-y-4 overflow-y-auto" style={{ maxHeight: '50%' }}>
                                    {/* Compact collection info card */}
                                    <div className="bg-neutral-600 border border-neutral-500 rounded-small px-3 py-2 space-y-1.5">
                                        {/* Name row */}
                                        <div className="flex items-center gap-2">
                                            {editingName ? (
                                                <div className="flex-1">
                                                    <input
                                                        ref={nameInputRef}
                                                        type="text"
                                                        className="w-full bg-neutral-500 border border-red-500 rounded-small px-2 py-1 text-sm text-neutral-100 focus:outline-none focus:border-red-400"
                                                        value={tempName}
                                                        maxLength={MAX_COLLECTION_NAME_LENGTH}
                                                        onChange={(e) => setTempName(e.target.value)}
                                                        onKeyDown={(e) => { if (e.key === 'Escape') { if (selectedCollection) setTempName(selectedCollection.name); setEditingName(false); } }}
                                                    />
                                                    <div className="text-right text-xs text-neutral-400 mt-0.5">{tempName.length}/{MAX_COLLECTION_NAME_LENGTH}</div>
                                                </div>
                                            ) : (
                                                <>
                                                    <span className="text-sm font-semibold text-neutral-100 flex-1 truncate">{selectedCollection.name}</span>
                                                    {selectedCollection.id !== '-1' && (
                                                        <span className={`px-1.5 rounded-small text-[10px] font-medium flex-shrink-0 ${selectedCollection.is_shared ? 'bg-red-700 text-neutral-100' : 'bg-neutral-700 text-neutral-300'}`}>
                                                            {selectedCollection.pii_phi_controlled ? 'PII/PHI controlled' : selectedCollection.is_shared ? 'Public' : 'Private'}
                                                        </span>
                                                    )}
                                                    {selectedCollection.id !== '-1' && isOwnedCollection && (
                                                        <button onClick={handleNameEdit} className="text-neutral-400 hover:text-red-500 transition-colors duration-200 flex-shrink-0" aria-label="Edit name" title="Edit name">
                                                            <i className="fas fa-edit text-xs" />
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        {/* Description row */}
                                        <div className="flex items-start gap-1">
                                            {editingDescription ? (
                                                <div className="flex-1">
                                                    <textarea
                                                        ref={descriptionTextareaRef}
                                                        className="w-full bg-neutral-500 border border-red-500 rounded-small px-2 py-1 text-xs text-neutral-100 focus:outline-none focus:border-red-400 resize-none"
                                                        value={tempDescription}
                                                        maxLength={MAX_COLLECTION_DESCRIPTION_LENGTH}
                                                        onChange={(e) => setTempDescription(e.target.value.replace(/\n/g, ' '))}
                                                        rows={2}
                                                        onKeyDown={(e) => { if (e.key === 'Escape') { if (selectedCollection) setTempDescription(selectedCollection.description); setEditingDescription(false); } }}
                                                    />
                                                    <div className="text-right text-xs text-neutral-400 mt-0.5">{tempDescription.length}/{MAX_COLLECTION_DESCRIPTION_LENGTH}</div>
                                                </div>
                                            ) : (
                                                <>
                                                    <p className="text-xs text-neutral-300 flex-1 line-clamp-2 leading-snug">
                                                        {selectedCollection.description || <span className="italic opacity-50">No description</span>}
                                                    </p>
                                                    {selectedCollection.id !== '-1' && isOwnedCollection && (
                                                        <button onClick={handleDescriptionEdit} className="text-neutral-400 hover:text-red-500 transition-colors duration-200 flex-shrink-0 mt-0.5" aria-label="Edit description" title="Edit description">
                                                            <i className="fas fa-edit text-xs" />
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        {/* Visibility row — admin only. Everyone
                                            else shares with named people below. */}
                                        {selectedCollection.id !== '-1' && isAdmin && isOwnedCollection && !selectedCollection.pii_phi_controlled && (
                                            <div className="flex items-center gap-2 pt-1.5 border-t border-neutral-500">
                                                <span className="text-xs text-neutral-400 flex-shrink-0" title="Public makes this collection readable by every account on this instance.">Visibility <i className="fas fa-info-circle opacity-60" /></span>
                                                <button type="button" onClick={() => setTempIsShared(false)} className={`px-2 py-0.5 rounded-small text-xs cursor-pointer transition-colors duration-150 ${!tempIsShared ? 'bg-neutral-300 text-neutral-900' : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-500'}`}>Private</button>
                                                <button type="button" onClick={() => setTempIsShared(true)} className={`px-2 py-0.5 rounded-small text-xs cursor-pointer transition-colors duration-150 ${tempIsShared ? 'bg-red-700 text-neutral-100' : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-500'}`}>Public</button>
                                            </div>
                                        )}

                                        {/* Sharing — grant named users access.
                                            Owner only: the backend refuses a
                                            re-share, so showing the control to
                                            a recipient would only mislead. */}
                                        {selectedCollection.id !== '-1' && canManageCollection && (
                                            <div className="pt-1.5 border-t border-neutral-500">
                                                <div className="flex items-center gap-2 mb-1.5">
                                                    <span className="text-xs text-neutral-400 flex-shrink-0" title="People you share with can read the documents in this collection. Edit also lets them add documents to it.">Shared with <i className="fas fa-info-circle opacity-60" /></span>
                                                    {tempIsShared && !selectedCollection.pii_phi_controlled && (
                                                        <span className="text-[10px] text-neutral-400 italic">Public — everyone can already read this</span>
                                                    )}
                                                </div>

                                                <div className="flex items-center gap-1.5 mb-1.5">
                                                    <select
                                                        value={shareUserId}
                                                        onChange={(e) => setShareUserId(e.target.value)}
                                                        className="flex-1 min-w-0 bg-neutral-700 text-neutral-100 text-xs rounded-small px-1.5 py-1 border border-neutral-500 outline-none cursor-pointer"
                                                        aria-label="User to share with"
                                                    >
                                                        <option value="">Select a user…</option>
                                                        {shareableUsers
                                                            .filter((u) => !shares.some((s) => s.user_id === u.id))
                                                            .map((u) => (
                                                                <option key={u.id} value={u.id}>
                                                                    {([u.first_name, u.last_name].filter(Boolean).join(' ') || u.email) + (u.pii_phi_eligible ? ' · PII/PHI eligible' : ' · masked only')}
                                                                </option>
                                                            ))}
                                                    </select>
                                                    <select
                                                        value={sharePermission}
                                                        onChange={(e) => setSharePermission(e.target.value as CollectionSharePermission)}
                                                        className="bg-neutral-700 text-neutral-100 text-xs rounded-small px-1.5 py-1 border border-neutral-500 outline-none cursor-pointer flex-shrink-0"
                                                        aria-label="Permission to grant"
                                                    >
                                                        <option value="view">View</option>
                                                        <option value="edit">Edit</option>
                                                        {selectedCollection.pii_phi_controlled && (
                                                            <option value="pii_phi_control">PII/PHI control</option>
                                                        )}
                                                    </select>
                                                    <button
                                                        type="button"
                                                        onClick={handleShare}
                                                        disabled={!shareUserId}
                                                        className="px-2 py-1 rounded-small text-xs flex-shrink-0 transition-colors duration-150 bg-red-700 text-neutral-100 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                                                    >
                                                        Share
                                                    </button>
                                                </div>

                                                {sharesLoading ? (
                                                    <p className="text-[10px] text-neutral-400 italic">Loading…</p>
                                                ) : shares.length === 0 ? (
                                                    <p className="text-[10px] text-neutral-400 italic">Not shared with anyone yet.</p>
                                                ) : (
                                                    <ul className="flex flex-col gap-1">
                                                        {shares.map((share) => (
                                                            <li key={share.user_id} className="flex items-center gap-2 text-xs text-neutral-200">
                                                                <span className="truncate flex-1 min-w-0">
                                                                    {share.email || share.user_id}{share.pii_phi_eligible ? ' · eligible' : ' · masked only'}
                                                                </span>
                                                                <span className="px-1.5 rounded-small text-[10px] font-medium bg-neutral-700 text-neutral-300 flex-shrink-0 uppercase">
                                                                    {share.permission}
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleUnshare(share.user_id)}
                                                                    className="bg-transparent border-0 cursor-pointer text-neutral-400 hover:text-red-500 transition-colors duration-200 flex-shrink-0"
                                                                    aria-label={`Revoke access for ${share.email || share.user_id}`}
                                                                    title="Revoke access"
                                                                >
                                                                    <i className="fas fa-times text-xs" />
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Export - compact single row */}
                                    <div className="flex items-center gap-2 px-1">
                                        <span className="text-xs text-neutral-400 flex-shrink-0" title={magazineTooltip}>Export .mag <i className="fas fa-info-circle opacity-60" /></span>
                                        {hasEncryptKey && (
                                            <label htmlFor="collection-export-encrypt" className="flex items-center gap-1 text-xs text-neutral-300 cursor-pointer flex-shrink-0">
                                                <input id="collection-export-encrypt" type="checkbox" checked={encryptExport} onChange={(e) => setEncryptExport(e.target.checked)} className="accent-red-600" />
                                                Encrypt
                                            </label>
                                        )}
                                        {hasSigningKey && (
                                            <label htmlFor="collection-export-sign" className="flex items-center gap-1 text-xs text-neutral-300 cursor-pointer flex-shrink-0">
                                                <input id="collection-export-sign" type="checkbox" checked={signExport} onChange={(e) => setSignExport(e.target.checked)} className="accent-red-600" />
                                                Sign
                                            </label>
                                        )}
                                        <button
                                            onClick={() => handleExportCollectionAsMagazine(selectedCollection!)}
                                            aria-label="Export collection as Magazine"
                                            title="Export collection as Magazine"
                                            disabled={isExportingMagazine}
                                            type="button"
                                            className={`ml-auto px-3 py-1 rounded-small text-xs font-medium transition-colors duration-200 flex-shrink-0 ${isExportingMagazine ? 'bg-neutral-500 text-neutral-300 cursor-not-allowed' : 'bg-red-600 hover:bg-red-500 text-neutral-100'}`}
                                        >
                                            {isExportingMagazine ? <><i className="fas fa-spinner fa-spin mr-1" />Exporting…</> : <><i className="fas fa-download mr-1" />Export</>}
                                        </button>
                                    </div>

                                    </div>{/* end flex-none top block */}

                                    {/* Assigned Files Section - fills remaining height, scrolls internally */}
                                    <div className="flex-1 flex flex-col overflow-hidden min-h-0 mt-4">
                                        <div className="flex items-center justify-between mb-3 flex-none">
                                            <h4 className="font-semibold text-neutral-100 text-sm">Assigned files</h4>
                                            {isOwnedCollection && selectedDocuments.length > 0 && (
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    {/* Bulk add to collection */}
                                                    <div className="relative">
                                                        <button
                                                            className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full transition-colors duration-200 ${showBulkAssignPicker ? 'bg-red-600 text-white' : 'bg-neutral-500 text-neutral-100 hover:bg-neutral-400'}`}
                                                            onClick={() => setShowBulkAssignPicker(p => !p)}
                                                            aria-label="Add selected files to a collection"
                                                            title="Add selected files to a collection"
                                                        >
                                                            <i className="fas fa-folder-plus" />
                                                            Add to ({selectedDocuments.length})
                                                        </button>
                                                        {showBulkAssignPicker && (
                                                            <div className="absolute right-0 top-7 z-20 bg-neutral-700 border border-neutral-400 rounded-small shadow-large min-w-[180px] max-h-48 overflow-y-auto">
                                                                <p className="text-xs text-neutral-300 px-3 pt-2 pb-1 font-semibold uppercase tracking-wide border-b border-neutral-500">Add to collection</p>
                                                                {collections.filter(c => c.id !== '-1').length === 0 ? (
                                                                    <p className="text-xs text-neutral-400 px-3 py-2 italic">No collections</p>
                                                                ) : (
                                                                    collections.filter(c => c.id !== '-1').map(c => (
                                                                        <button
                                                                            key={c.id}
                                                                            className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm text-neutral-200 hover:bg-neutral-600 transition-colors duration-150"
                                                                            onClick={() => handleBulkAssignToCollection(c.id)}
                                                                        >
                                                                            <i className="fas fa-folder text-neutral-400 text-xs" />
                                                                            <span className="truncate">{c.name}</span>
                                                                        </button>
                                                                    ))
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {/* Hide remove button for "All" collection */}
                                                    {selectedCollection?.id !== '-1' && (
                                                        <button
                                                            className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-neutral-500 text-neutral-100 hover:bg-neutral-400 transition-colors duration-200"
                                                            onClick={() => {
                                                                setBulkOperation('remove');
                                                                setShowBulkDeleteConfirmation(true);
                                                            }}
                                                            aria-label="Remove selected files from collection"
                                                            title="Remove selected files from collection"
                                                        >
                                                            <i className="fas fa-times" />
                                                            Remove ({selectedDocuments.length})
                                                        </button>
                                                    )}
                                                    <button
                                                        className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-red-700 text-neutral-100 hover:bg-red-600 transition-colors duration-200"
                                                        onClick={() => {
                                                            setBulkOperation('delete');
                                                            setShowBulkDeleteConfirmation(true);
                                                        }}
                                                        aria-label="Delete selected files permanently"
                                                        title="Delete selected files permanently"
                                                    >
                                                        <i className="fas fa-trash" />
                                                        Delete ({selectedDocuments.length})
                                                    </button>
                                </div>
                                            )}
                            </div>

                                        <div className="flex-1 overflow-y-auto">
                                        {visibleDocuments.length === 0 && addedFiles.size === 0 ? (
                                            <div className="text-neutral-300 text-sm italic py-4">
                                                No documents assigned
                                    </div>
                                ) : (
                                            <div className="space-y-2">
                                                {/* Select All Checkbox */}
                                                {isOwnedCollection && visibleDocuments.length > 0 && (
                                                    <div className="flex items-center py-2 px-3 bg-neutral-600 rounded-small border-b border-neutral-500">
                                                        <input 
                                                            type="checkbox" 
                                                            checked={selectedDocuments.length === visibleDocuments.length && visibleDocuments.length > 0}
                                                            onChange={handleSelectAllDocuments}
                                                            className="mr-3 accent-red-600"
                                                        />
                                                        <span className="text-neutral-200 text-sm font-medium">
                                                            {selectedDocuments.length === visibleDocuments.length && visibleDocuments.length > 0 
                                                                ? 'Deselect All' 
                                                                : 'Select All'
                                                            } ({visibleDocuments.length})
                                                        </span>
                                                    </div>
                                                )}
                                                
                                                {/* Existing documents */}
                                        {visibleDocuments.map(document => {
                                                    // Which non-All collections contain this file
                                                    const docCollections = collections.filter(
                                                        c => c.id !== '-1' && (c.files || []).some(f => f.id === document.id)
                                                    );
                                                    // Assignable collections: real collections NOT already containing this file
                                                    const assignableCollections = collections.filter(
                                                        c => c.id !== '-1' && !(c.files || []).some(f => f.id === document.id)
                                                    );
                                                    return (
                                                    <div key={document.id} className="py-2 px-3 bg-neutral-500 rounded-small hover:bg-neutral-400 transition-colors duration-200">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center flex-1 min-w-0">
                                                                {isOwnedCollection && (
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedDocuments.includes(document.id)}
                                                                        onChange={() => handleToggleDocumentSelection(document.id)}
                                                                        className="mr-3 accent-red-600 flex-shrink-0"
                                                                    />
                                                                )}
                                                                <span className="text-neutral-100 text-sm truncate">{document.filename}</span>
                                                                {selectedCollection?.pii_phi_controlled && (
                                                                    <span className="ml-2 text-[10px] text-neutral-300 flex-shrink-0">
                                                                        {document.can_view_original ? 'original available' : 'masked view'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                                                <button
                                                                    className="text-neutral-300 hover:text-neutral-100 transition-colors duration-200"
                                                                    onClick={() => window.open(apiClient.files.getFileContentUrl(document.id), '_blank', 'noopener,noreferrer')}
                                                                    aria-label={document.can_view_original ? 'Open protected original' : 'Open masked document'}
                                                                    title={document.can_view_original ? 'Open protected original (audited)' : 'Open masked document'}
                                                                >
                                                                    <i className="fas fa-external-link-alt text-xs" />
                                                                </button>
                                                            {isOwnedCollection && (
                                                                <>
                                                                {/* Collection assign picker toggle */}
                                                                <div className="relative">
                                                                    <button
                                                                        className={`text-xs px-2 py-0.5 rounded transition-colors duration-200 ${
                                                                            assignPickerDocId === document.id
                                                                                ? 'bg-red-600 text-white'
                                                                                : 'text-neutral-300 hover:text-neutral-100 bg-neutral-600 hover:bg-neutral-500'
                                                                        }`}
                                                                        onClick={() => setAssignPickerDocId(prev => prev === document.id ? null : document.id)}
                                                                        title="Assign to collections"
                                                                        aria-label="Assign to collections"
                                                                    >
                                                                        <i className="fas fa-folder-plus text-xs" />
                                                                    </button>
                                                                    {assignPickerDocId === document.id && (
                                                                        <div className="absolute right-0 top-7 z-20 bg-neutral-700 border border-neutral-400 rounded-small shadow-large min-w-[180px] max-h-48 overflow-y-auto">
                                                                            <p className="text-xs text-neutral-300 px-3 pt-2 pb-1 font-semibold uppercase tracking-wide border-b border-neutral-500">Collections</p>
                                                                            {collections.filter(c => c.id !== '-1').length === 0 ? (
                                                                                <p className="text-xs text-neutral-400 px-3 py-2 italic">No collections</p>
                                                                            ) : (
                                                                                collections
                                                                                    .filter(c => c.id !== '-1')
                                                                                    .map(c => {
                                                                                        const inCollection = (c.files || []).some(f => f.id === document.id);
                                                                                        return (
                                                                                            <button
                                                                                                key={c.id}
                                                                                                className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm hover:bg-neutral-600 transition-colors duration-150"
                                                                                                onClick={() => handleToggleDocInCollection(document.id, c.id, inCollection)}
                                                                                            >
                                                                                                <i className={`fas ${inCollection ? 'fa-check-square text-red-400' : 'fa-square text-neutral-400'} text-xs`} />
                                                                                                <span className={`truncate ${inCollection ? 'text-neutral-100' : 'text-neutral-300'}`}>
                                                                                                    {c.name}
                                                                                                </span>
                                                                                            </button>
                                                                                        );
                                                                                    })
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {/* Hide remove button for "All" collection */}
                                                                {selectedCollection?.id !== '-1' && (
                                                                    <button
                                                                        className="text-neutral-300 hover:text-red-500 transition-colors duration-200"
                                                                        onClick={() => handleRemoveDocument(document.id)}
                                                                        aria-label="Remove file from collection"
                                                                        title="Remove file from collection"
                                                                    >
                                                                        <i className="fas fa-times text-xs" />
                                                                    </button>
                                                                )}
                                                                <button
                                                                    className="text-neutral-300 hover:text-red-500 transition-colors duration-200"
                                                                    onClick={() => handleDeleteDocument(document)}
                                                                    aria-label="Delete file permanently"
                                                                    title="Delete file permanently"
                                                                >
                                                                    <i className="fas fa-trash text-xs" />
                                                                </button>
                                                                </>
                                                            )}
                                                            </div>
                                                        </div>
                                                        {/* Collection membership chips */}
                                                        {docCollections.length > 0 && (
                                                            <div className="flex flex-wrap gap-1 mt-1.5 ml-6">
                                                                {docCollections.map(c => (
                                                                    <span key={c.id} className="text-[10px] px-1.5 py-0.5 rounded-full bg-neutral-600 text-neutral-300">
                                                                        {c.name}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {assignableCollections.length > 0 && assignPickerDocId !== document.id && docCollections.length === 0 && (
                                                            <p className="text-[10px] text-neutral-400 mt-1 ml-6 italic">Not in any collection</p>
                                                        )}
                                                    </div>
                                                    );
                                                })}
                                                
                                                {/* Newly added files */}
                                                {Array.from(addedFileDetails.entries()).map(([path, file]) => (
                                                    <div key={path} className="flex items-center justify-between py-2 px-3 bg-red-600/20 border border-red-600/40 rounded-small">
                                                        <div className="flex items-center">
                                                            <span className="text-red-400 text-xs mr-3">NEW</span>
                                                            <span className="text-neutral-100 text-sm">{file.name}</span>
                                                        </div>
                                                        <button
                                                            className="text-neutral-300 hover:text-red-500 transition-colors duration-200"
                                                            onClick={() => {
                                                                setAddedFiles(prev => {
                                                                    const newSet = new Set(prev);
                                                                    newSet.delete(path);
                                                                    return newSet;
                                                                });
                                                                setAddedFileDetails(prev => {
                                                                    const newMap = new Map(prev);
                                                                    newMap.delete(path);
                                                                    return newMap;
                                                                });
                                                            }}
                                                            aria-label="Remove file from pending additions"
                                                            title="Remove file from pending additions"
                                                        >
                                                            <i className="fas fa-times text-xs" />
                                                        </button>
                                                    </div>
                                                ))}
                                    </div>
                                )}
                                        </div>{/* end flex-1 overflow-y-auto doc list */}
                            </div>
                                </>
                            )}
                        </div>

                        {/* Right Panel - File Browser */}
                        <div className="w-1/2 bg-neutral-500 rounded-medium border border-neutral-400 overflow-hidden">
                            {!canManageCollection ? (
                                <div className="w-full h-full flex flex-col items-center justify-center bg-neutral-800 text-neutral-300 p-4 text-center">
                                    <i className="fas fa-lock text-3xl mb-3 text-neutral-500" />
                                    <h3 className="text-base font-medium mb-1">View-only collection</h3>
                                    <p className="text-xs text-neutral-400">You can open its files, but a collection controller manages protected uploads and membership.</p>
                                </div>
                            ) : FileBrowser ? (
                                <FileBrowser
                                    selectedCollectionId={selectedCollection?.id || null}
                                    onAddFile={handleAddFileFromBrowser}
                                    addedFiles={addedFiles}
                                />
                            ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center bg-neutral-800 text-neutral-300 p-4">
                                    <i className="fas fa-cloud-upload-alt text-4xl mb-4 text-neutral-500" />
                                    <h3 className="text-lg font-medium mb-2">File Browser Unavailable</h3>
                                </div>
                            )}
                        </div>
                        </div>
                    </div>
                    
                {/* Footer */}
                <div className="flex justify-end gap-3 pr-lg pb-lg">
                    {uploadProgress.isUploading ? (
                        // Show cancel upload button during upload
                            <button 
                            className={`px-6 py-2 rounded-small font-medium transition-colors duration-200 ${
                                uploadProgress.isCancelling
                                    ? 'bg-neutral-500 text-neutral-300 cursor-not-allowed' 
                                    : 'bg-neutral-500 hover:bg-neutral-400 text-neutral-100'
                            }`}
                            onClick={handleCancelUpload}
                            disabled={uploadProgress.isCancelling}
                            aria-label="Cancel upload"
                            title="Cancel upload"
                        >
                            {uploadProgress.isCancelling ? 'Cancelling...' : 'Cancel Upload'}
                        </button>
                    ) : (
                        // Show regular cancel button when not uploading
                        <button 
                            className={`px-6 py-2 rounded-small font-medium transition-colors duration-200 ${
                                hasChanges && !loading
                                    ? 'bg-neutral-500 hover:bg-neutral-400 text-neutral-100' 
                                    : 'bg-neutral-500 text-neutral-300 cursor-not-allowed'
                            }`}
                                onClick={handleCancelChanges}
                            disabled={!hasChanges || loading}
                                aria-label="Cancel changes"
                                title="Cancel changes"
                            >
                                Cancel
                            </button>
                    )}
                            <button
                        className={`${uploadProgress.isUploading || importProgress.isImporting ? 'px-4 py-2' : 'px-6 py-2'} rounded-small font-medium transition-colors duration-200 ${
                            hasChanges && !loading && !uploadProgress.isUploading && !importProgress.isImporting
                                ? 'bg-red-600 hover:bg-red-500 text-neutral-100'
                                : 'bg-neutral-500 text-neutral-300 cursor-not-allowed'
                        }`}
                                onClick={handleApplyChanges}
                        disabled={!hasChanges || loading || uploadProgress.isUploading || importProgress.isImporting}
                        aria-label="Save changes"
                        title="Save changes"
                    >
                        {uploadProgress.isUploading ? (
                            <div className="flex items-center gap-3 min-w-0">
                                <i className="fas fa-spinner fa-spin flex-shrink-0" />
                                <div className="flex flex-col items-start min-w-0 flex-1">
                                    <span className="text-xs truncate max-w-[200px] mb-1">
                                        {uploadProgress.currentFile ? `Uploading: ${uploadProgress.currentFile}` : 'Preparing...'}
                                    </span>
                                    <div className="flex items-center gap-2 w-full">
                                        <span className="text-xs opacity-75 flex-shrink-0">
                                            {isDragDropUpload
                                                ? `${currentFileIndex}/${totalFilesToUpload}`
                                                : `${uploadProgress.currentIndex}/${uploadProgress.totalFiles}`
                                            }
                                        </span>
                                        <div className="flex-1 bg-neutral-500 rounded-full h-2 overflow-hidden min-w-[80px]">
                                            <div
                                                className="h-full bg-red-600 transition-all duration-300 ease-out"
                                                style={{ width: `${uploadProgress.percentage}%` }}
                                             />
                        </div>
                                        <span className="text-xs opacity-75 flex-shrink-0">
                                            {uploadProgress.percentage}%
                                        </span>
                    </div>
                                </div>
                            </div>
                        ) : importProgress.isImporting ? (
                            <div className="flex items-center gap-3 min-w-0">
                                <i className="fas fa-spinner fa-spin flex-shrink-0"></i>
                                <div className="flex flex-col items-start min-w-0 flex-1">
                                    <span className="text-xs truncate max-w-[200px] mb-1">
                                        {importProgress.currentFile ? `Importing: ${importProgress.currentFile}` : 'Preparing...'}
                                    </span>
                                    <div className="flex items-center gap-2 w-full">
                                        <span className="text-xs opacity-75 flex-shrink-0">
                                            {`${importProgress.currentIndex}/${importProgress.totalFiles}`}
                                        </span>
                                        <div className="flex-1 bg-neutral-500 rounded-full h-2 overflow-hidden min-w-[80px]">
                                            <div
                                                className="h-full bg-red-600 transition-all duration-300 ease-out"
                                                style={{ width: `${importProgress.percentage}%` }}
                                            ></div>
                                        </div>
                                        <span className="text-xs opacity-75 flex-shrink-0">
                                            {importProgress.percentage}%
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ) : loading ? (
                            'Saving...'
                        ) : (
                            'Save'
                        )}
                    </button>
                </div>
            </div>

            {/* Confirmation dialog for unsaved changes */}
            {showConfirmation && (
                <div className="fixed top-0 left-0 right-0 bottom-0 bg-neutral-700/50 flex justify-center items-center z-[1100]" onClick={handleCancelClose}>
                    <div className="bg-neutral-700 rounded-medium p-lg max-w-md w-full mx-4 shadow-large border-2 border-neutral-400" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-neutral-100 mb-4">Unsaved Changes</h3>
                        <p className="text-neutral-200 text-sm mb-6">
                            {tempCollection 
                                ? `You have unsaved changes to the "${selectedCollection?.name}" collection. Do you want to switch to "${tempCollection.name}" without saving?` 
                                : 'You have unsaved changes. Are you sure you want to close without saving?'}
                        </p>
                        <div className="flex justify-end gap-3">
                            <button 
                                className="bg-neutral-500 hover:bg-neutral-400 text-neutral-100 px-4 py-2 rounded-small font-medium transition-colors duration-200"
                                onClick={handleCancelClose}
                                ref={closeConfirmButtonRef}
                                aria-label="Go back to editing"
                                title="Go back to editing"
                            >
                                Go Back
                            </button>
                            <button 
                                className="bg-red-500 hover:bg-red-600 text-neutral-100 px-4 py-2 rounded-small font-medium transition-colors duration-200"
                                onClick={handleConfirmClose}
                                aria-label="Close without saving"
                                title="Close without saving"
                            >
                                {tempCollection ? "Switch Without Saving" : "Close Without Saving"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Confirmation dialog for permanent document deletion */}
            {showDeleteConfirmation && documentToDelete && (
                <div className="fixed top-0 left-0 right-0 bottom-0 bg-neutral-700/50 flex justify-center items-center z-[1100]" onClick={!isDeleting ? handleCancelDeleteConfirmation : undefined}>
                    <div className="bg-neutral-700 rounded-medium p-lg max-w-md w-full mx-4 shadow-large border-2 border-neutral-400" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-neutral-100 mb-4">Delete Document Permanently</h3>
                        <p className="text-neutral-200 text-sm mb-6">
                            Are you sure you want to permanently delete <strong className="text-neutral-100">{documentToDelete.filename}</strong>?
                            This document will be removed from all collections but remain on your system.
                        </p>
                        {isDeleting && (
                            <div className="bg-neutral-600 border border-neutral-500 rounded-small p-3 mb-4 flex items-center gap-3">
                                <i className="fas fa-spinner fa-spin text-red-500" />
                                <div className="text-neutral-200 text-sm">
                                    <p className="font-medium">Deleting document...</p>
                                    <p className="text-xs text-neutral-300 mt-1">This may take a moment for large files.</p>
                                </div>
                            </div>
                        )}
                        <div className="flex justify-end gap-3">
                            <button 
                                className={`px-4 py-2 rounded-small font-medium transition-colors duration-200 ${
                                    isDeleting 
                                        ? 'bg-neutral-500 text-neutral-300 cursor-not-allowed' 
                                        : 'bg-neutral-500 hover:bg-neutral-400 text-neutral-100'
                                }`}
                                onClick={handleCancelDeleteConfirmation}
                                disabled={isDeleting}
                                ref={deleteConfirmButtonRef}
                                aria-label="Cancel deletion"
                                title="Cancel deletion"
                            >
                                Cancel
                            </button>
                            <button 
                                className={`px-4 py-2 rounded-small font-medium transition-colors duration-200 ${
                                    isDeleting
                                        ? 'bg-neutral-500 text-neutral-300 cursor-not-allowed'
                                        : 'bg-red-500 hover:bg-red-600 text-neutral-100'
                                }`}
                                onClick={handleConfirmDelete}
                                disabled={isDeleting}
                                aria-label="Confirm deletion"
                                title="Confirm deletion"
                            >
                                {isDeleting ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin mr-2" />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-trash mr-2" />
                                        Delete Permanently
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create collection modal */}
            {showCreateModal && (
                <div className="fixed top-0 left-0 right-0 bottom-0 bg-neutral-700/50 flex justify-center items-center z-[1100]" onClick={handleCloseCreateModal}>
                    <div className="bg-neutral-700 rounded-medium p-lg max-w-md w-full mx-4 shadow-large border-2 border-neutral-400" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-neutral-100 mb-4">Create New Collection</h3>
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <span className="text-neutral-100 text-xs font-medium uppercase tracking-wide">Collection Name</span>
                                <input 
                                        ref={createNameInputRef}
                                        type="text" 
                                        className="w-full bg-neutral-600 border-2 border-neutral-500 rounded-small px-3 py-2 text-neutral-100 focus:outline-none focus:border-red-400 placeholder-neutral-300"
                                        value={newCollectionName}
                                        maxLength={MAX_COLLECTION_NAME_LENGTH}
                                        onChange={(e) => setNewCollectionName(e.target.value)}
                                        placeholder="Enter collection name"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && newCollectionName.trim()) {
                                                handleCreateCollection();
                                            } else if (e.key === 'Escape') {
                                                handleCloseCreateModal();
                                            }
                                        }}
                                    />
                                    <div className="text-right text-xs text-neutral-400 mt-1">
                                        {newCollectionName.length}/{MAX_COLLECTION_NAME_LENGTH}
                                    </div>
                            </div>
                            <div className="space-y-2">
                                <span className="text-neutral-100 text-xs font-medium uppercase tracking-wide">Description</span>
                                <textarea 
                                        ref={createDescriptionTextareaRef}
                                        className="w-full bg-neutral-600 border-2 border-neutral-500 rounded-small px-3 py-2 text-neutral-100 focus:outline-none focus:border-red-400 resize-none placeholder-neutral-300"
                                        value={newCollectionDescription}
                                        maxLength={MAX_COLLECTION_DESCRIPTION_LENGTH}
                                        onChange={(e) => setNewCollectionDescription(e.target.value.replace(/\n/g, ' '))}
                                        placeholder="Enter collection description (optional)"
                                        rows={3}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Escape') {
                                                handleCloseCreateModal();
                                            }
                                        }}
                                    />
                                    <div className="text-right text-xs text-neutral-400 mt-1">
                                        {newCollectionDescription.length}/{MAX_COLLECTION_DESCRIPTION_LENGTH}
                                    </div>
                            </div>
                            {isPiiPhiEligible && (
                            <div className="bg-neutral-600 border border-neutral-500 rounded-small px-3 py-2">
                                <label htmlFor="new-collection-pii-phi" className="flex items-center gap-2 text-neutral-100 text-sm font-medium cursor-pointer">
                                    <input
                                        id="new-collection-pii-phi"
                                        type="checkbox"
                                        checked={newCollectionIsPiiPhiControlled}
                                        onChange={(event) => {
                                            setNewCollectionIsPiiPhiControlled(event.target.checked);
                                            if (event.target.checked) setNewCollectionIsShared(false);
                                        }}
                                    />
                                    PII/PHI-controlled collection
                                </label>
                                <p className="text-neutral-300 text-xs mt-2 mb-0">
                                    Uploads keep an encrypted original. Eligible members may open it and use original context with the model; other members receive masked documents, model context, and citations.
                                </p>
                            </div>
                            )}
                            {/* Publishing to the whole instance is an admin
                                decision. Everyone else creates the collection
                                private and shares it with named users after. */}
                            {isAdmin && !newCollectionIsPiiPhiControlled && (
                            <div className="bg-neutral-600 border border-neutral-500 rounded-small px-3 py-2">
                                <span className="text-neutral-100 text-xs font-medium uppercase tracking-wide">Visibility</span>
                                <div className="flex items-center gap-2 mt-2">
                                    <button
                                        type="button"
                                        className={`px-3 py-1 rounded-small border cursor-pointer text-sm ${
                                            !newCollectionIsShared
                                                ? 'bg-neutral-300 text-neutral-900 border-neutral-300'
                                                : 'bg-neutral-700 text-neutral-200 border-neutral-500 hover:bg-neutral-500'
                                        }`}
                                        onClick={() => setNewCollectionIsShared(false)}
                                    >
                                        Private
                                    </button>
                                    <button
                                        type="button"
                                        className={`px-3 py-1 rounded-small border cursor-pointer text-sm ${
                                            newCollectionIsShared
                                                ? 'bg-red-700 text-neutral-100 border-red-700'
                                                : 'bg-neutral-700 text-neutral-200 border-neutral-500 hover:bg-neutral-500'
                                        }`}
                                        onClick={() => setNewCollectionIsShared(true)}
                                    >
                                        Public
                                    </button>
                                </div>
                                <p className="text-neutral-300 text-xs mt-2 mb-0">
                                    Public collections are readable by every account on this instance. Private collections are visible only to you and anyone you share them with.
                                </p>
                            </div>
                            )}
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button 
                                className="bg-neutral-500 hover:bg-neutral-400 text-neutral-100 px-4 py-2 rounded-small font-medium transition-colors duration-200"
                                onClick={handleCloseCreateModal}
                                disabled={creatingCollection}
                                aria-label="Cancel creating collection"
                                title="Cancel creating collection"
                            >
                                Cancel
                            </button>
                            <button 
                                className={`px-4 py-2 rounded-small font-medium transition-colors duration-200 ${
                                    newCollectionName.trim() && !creatingCollection
                                        ? 'bg-red-600 hover:bg-red-500 text-neutral-100'
                                        : 'bg-neutral-500 text-neutral-300 cursor-not-allowed'
                                }`}
                                onClick={handleCreateCollection}
                                disabled={!newCollectionName.trim() || creatingCollection}
                                aria-label="Create collection"
                                title="Create collection"
                            >
                                {creatingCollection ? 'Creating...' : 'Create'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation dialog for collection deletion */}
            {showDeleteCollectionConfirmation && collectionToDelete && (
                <div className="fixed top-0 left-0 right-0 bottom-0 bg-neutral-700/50 flex justify-center items-center z-[1100]" onClick={handleCancelDeleteCollectionConfirmation}>
                    <div className="bg-neutral-700 rounded-medium p-lg max-w-md w-full mx-4 shadow-large border-2 border-neutral-400" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-neutral-100 mb-4">Delete Collection</h3>
                        <p className="text-neutral-200 text-sm mb-6">
                            Are you sure you want to delete the collection <strong className="text-neutral-100">&quot;{collectionToDelete.name}&quot;</strong>?
                            <br /><br />
                            This will remove the collection and all document associations, but the documents themselves will remain in your library.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button 
                                className="bg-neutral-500 hover:bg-neutral-400 text-neutral-100 px-4 py-2 rounded-small font-medium transition-colors duration-200"
                                onClick={handleCancelDeleteCollectionConfirmation}
                                aria-label="Cancel collection deletion"
                                title="Cancel collection deletion"
                            >
                                Cancel
                            </button>
                            <button 
                                className="bg-red-500 hover:bg-red-600 text-neutral-100 px-4 py-2 rounded-small font-medium transition-colors duration-200"
                                onClick={handleConfirmDeleteCollection}
                                aria-label="Confirm collection deletion"
                                title="Confirm collection deletion"
                            >
                                <i className="fas fa-trash mr-2" />
                                Delete Collection
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation dialog for bulk operations */}
            {showBulkDeleteConfirmation && (
                <div className="fixed top-0 left-0 right-0 bottom-0 bg-neutral-700/50 flex justify-center items-center z-[1100]" onClick={!isDeleting ? handleCancelBulkOperation : undefined}>
                    <div className="bg-neutral-700 rounded-medium p-lg max-w-md w-full mx-4 shadow-large border-2 border-neutral-400" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-neutral-100 mb-4">
                            {bulkOperation === 'remove' ? 'Remove Files from Collection' : 'Delete Files Permanently'}
                        </h3>
                        <p className="text-neutral-200 text-sm mb-6">
                            {bulkOperation === 'remove' 
                                ? `Are you sure you want to remove ${selectedDocuments.length} file(s) from this collection? The files will remain in your library.`
                                : `Are you sure you want to permanently delete ${selectedDocuments.length} file(s)? This action cannot be undone and the files will be removed from all collections.`
                            }
                        </p>
                        {isDeleting && bulkOperation === 'delete' && (
                            <div className="bg-neutral-600 border border-neutral-500 rounded-small p-3 mb-4 flex items-center gap-3">
                                <i className="fas fa-spinner fa-spin text-red-500" />
                                <div className="text-neutral-200 text-sm">
                                    <p className="font-medium">Deleting {selectedDocuments.length} document(s)...</p>
                                    <p className="text-xs text-neutral-300 mt-1">This may take a moment for large files.</p>
                                </div>
                            </div>
                        )}
                        <div className="flex justify-end gap-3">
                            <button 
                                className={`px-4 py-2 rounded-small font-medium transition-colors duration-200 ${
                                    isDeleting 
                                        ? 'bg-neutral-500 text-neutral-300 cursor-not-allowed' 
                                        : 'bg-neutral-500 hover:bg-neutral-400 text-neutral-100'
                                }`}
                                onClick={handleCancelBulkOperation}
                                disabled={isDeleting}
                                aria-label="Cancel bulk operation"
                                title="Cancel bulk operation"
                            >
                                Cancel
                            </button>
                            <button 
                                className={`px-4 py-2 rounded-small font-medium transition-colors duration-200 ${
                                    isDeleting
                                        ? 'bg-neutral-500 text-neutral-300 cursor-not-allowed'
                                        : 'bg-red-500 hover:bg-red-600 text-neutral-100'
                                }`}
                                onClick={handleConfirmBulkOperation}
                                disabled={isDeleting}
                                aria-label="Confirm bulk operation"
                                title="Confirm bulk operation"
                            >
                                {isDeleting && bulkOperation === 'delete' ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin mr-2" />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <i className={`fas ${bulkOperation === 'remove' ? 'fa-times' : 'fa-trash'} mr-2`} />
                                        {bulkOperation === 'remove' ? 'Remove Files' : 'Delete Files'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Drag and Drop Confirmation Modal */}
            {showDragDropModal && (
                <DragAndDropModal
                    files={pendingDroppedFiles}
                    folders={pendingDroppedFolders}
                    collections={collections}
                    isVLMSupported={false}
                    onConfirm={handleDragDropConfirm}
                    onCancel={handleDragDropCancel}
                />
            )}

            {/* Drag and Drop Overlay */}
            {isDraggingOver && (
                <div 
                    className="fixed inset-0 z-[1200] pointer-events-none"
                >
                    <div className="absolute inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center">
                        <div className="bg-neutral-600 border-2 border-dashed border-red-500 rounded-xl p-12 mx-8 shadow-2xl animate-pulse">
                            <div className="text-center">
                                <i className="fas fa-cloud-upload-alt text-6xl text-red-500 mb-4" />
                                <h3 className="text-2xl font-bold text-neutral-100 mb-2">
                                    Drop Folders Here
                                </h3>
                                <p className="text-neutral-200 text-sm">
                                    Create a new collection for each folder with its files
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};
